import React, { useCallback, useEffect, useRef, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import UserManagement from './user-management/UserManagement';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';
const MARKET_REQUEST_TIMEOUT_MS = Number(process.env.REACT_APP_MARKET_REQUEST_TIMEOUT_MS || 70000);
const MARKET_ROW_OPTIONS = [25, 50, 75, 100];

// Helper to format large numbers (Market Cap and Volume) into B/M format
function formatLargeNumber(value) {
  if (!value) return 'N/A';
  
  // If it's already a string with B/M/T, return as is
  if (typeof value === 'string' && /[BMT]/.test(value)) {
    return value;
  }
  
  // Convert to number if it's a string
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : value;
  
  if (isNaN(num)) return value;
  
  if (num >= 1e12) {
    return `${(num / 1e12).toFixed(2)}T`;
  } else if (num >= 1e9) {
    return `${(num / 1e9).toFixed(2)}B`;
  } else if (num >= 1e6) {
    return `${(num / 1e6).toFixed(2)}M`;
  } else if (num >= 1e3) {
    return `${(num / 1e3).toFixed(2)}K`;
  } else {
    return num.toLocaleString();
  }
}

// Helper to parse simple asterisk-based formatting from AI output
// *** Heading Level 1 (strong emphasis)
// **  Heading Level 2 (medium emphasis)
// *   Bullet list item
// Other lines become paragraphs. Consecutive single * lines are grouped into a single <ul>.
function parseAiAnalysis(text) {
  if (!text) return null;
  
  const lines = text.split(/\r?\n/);
  const sections = [];
  let currentSection = null;
  let listBuffer = [];
  let contentBuffer = [];

  // Define specific color schemes for the six AI analysis sections - Professional subtle colors
  const sectionColorMap = {
    'brief analysis': { 
      bg: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)', 
      border: '#2563eb', 
      icon: '📊', 
      textColor: '#ffffff', 
      contentBg: '#dbeafe',
      name: 'Brief Analysis of the Stock\'s Current Trend' 
    },
    'support and resistance': { 
      bg: 'linear-gradient(135deg, #4ade80 0%, #86efac 100%)', 
      border: '#22c55e', 
      icon: '🎯', 
      textColor: '#ffffff', 
      contentBg: '#dcfce7',
      name: 'Key Support and Resistance Levels' 
    },
    'swing trading opportunities': { 
      bg: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)', 
      border: '#059669', 
      icon: '💰', 
      textColor: '#ffffff', 
      contentBg: '#d1fae5',
      name: 'Potential Swing Trading Opportunities' 
    },
    'risk assessment': { 
      bg: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)', 
      border: '#d97706', 
      icon: '⚠️', 
      textColor: '#ffffff', 
      contentBg: '#fef3c7',
      name: 'Risk Assessment for Swing Trading' 
    },
    'recommended timeframe': { 
      bg: 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)', 
      border: '#db2777', 
      icon: '📈', 
      textColor: '#ffffff', 
      contentBg: '#fce7f3',
      name: 'Recommended Timeframe for Swing Trades' 
    },
    'prediction': { 
      bg: 'linear-gradient(135deg, #14b8a6 0%, #2dd4bf 100%)', 
      border: '#0d9488', 
      icon: '💡', 
      textColor: '#ffffff', 
      contentBg: '#ccfbf1',
      name: 'Prediction for the Stock in Next Two Weeks' 
    }
  };

  // Function to match section title to color scheme
  const getColorScheme = (title) => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('brief analysis') || lowerTitle.includes('current trend')) {
      return sectionColorMap['brief analysis'];
    } else if (lowerTitle.includes('support') && lowerTitle.includes('resistance')) {
      return sectionColorMap['support and resistance'];
    } else if (lowerTitle.includes('swing trading opportunities') || lowerTitle.includes('potential swing')) {
      return sectionColorMap['swing trading opportunities'];
    } else if (lowerTitle.includes('risk assessment')) {
      return sectionColorMap['risk assessment'];
    } else if (lowerTitle.includes('recommended timeframe') || lowerTitle.includes('timeframe for swing')) {
      return sectionColorMap['recommended timeframe'];
    } else if (lowerTitle.includes('prediction') || lowerTitle.includes('next two weeks')) {
      return sectionColorMap['prediction'];
    }
    // Default fallback
    return sectionColorMap['brief analysis'];
  };

  let currentColorScheme = null;

  // Aggressive helper function to clean ALL asterisks from text
  const cleanText = (str) => {
    if (!str) return '';
    // Remove all asterisks (single, double, triple, or more)
    // Then normalize multiple spaces to single space
    return str
      .replace(/\*\*\*/g, '')  // Remove triple asterisks
      .replace(/\*\*/g, '')    // Remove double asterisks
      .replace(/\*/g, '')      // Remove single asterisks
      .replace(/\s+/g, ' ')    // Normalize whitespace
      .trim();
  };

  const flushList = () => {
    if (listBuffer.length) {
      contentBuffer.push(
        <ul className="mb-2 ps-3" key={"list" + contentBuffer.length}>
          {listBuffer.map((item, i) => (
            <li key={"li" + i} className="mb-1">{cleanText(item)}</li>
          ))}
        </ul>
      );
      listBuffer = [];
    }
  };

  const flushSection = () => {
    if (currentSection && contentBuffer.length > 0) {
      const colorScheme = currentColorScheme || getColorScheme(currentSection);
      sections.push(
        <div 
          key={"section" + sections.length} 
          className="mb-3 p-3 rounded shadow-sm"
          style={{
            background: colorScheme.bg,
            border: `3px solid ${colorScheme.border}`,
            borderLeft: `6px solid ${colorScheme.border}`,
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
        >
          <h3 className="h5 fw-bold mb-3 d-flex align-items-center" style={{ color: colorScheme.textColor, fontWeight: '700' }}>
            <span className="me-2" style={{ fontSize: '1.3rem' }}>{colorScheme.icon}</span>
            {cleanText(currentSection)}
          </h3>
          <div className="p-2 rounded" style={{ backgroundColor: colorScheme.contentBg }}>
            {contentBuffer}
          </div>
        </div>
      );
      contentBuffer = [];
      currentColorScheme = null;
    }
  };

  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    
    // Handle markdown-style hash headings: #, ##, ###, ####, etc.
    const hashMatch = line.match(/^(#{1,6})\s+(.*)/);
    if (hashMatch) {
      const level = hashMatch[1].length;
      let content = cleanText(hashMatch[2]);
      
      if (level === 1 || level === 2) {
        // This is a main section heading
        flushList();
        flushSection();
        currentSection = content;
        currentColorScheme = getColorScheme(content);
      } else {
        // Sub-heading within a section
        flushList();
        if (content) {
          contentBuffer.push(
            <div key={"h" + contentBuffer.length} className="fw-bold text-dark mb-2 mt-2">
              {content}
            </div>
          );
        }
      }
      return;
    }

    const match = line.match(/^(\*{1,3})\s+(.*)/);
    if (match) {
      const stars = match[1].length;
      let content = cleanText(match[2]);
      
      if (stars === 3 || stars === 2) {
        // This is a main section heading
        flushList();
        flushSection();
        currentSection = content;
        currentColorScheme = getColorScheme(content);
      } else if (stars === 1) {
        // bullet item - add to buffer (will be cleaned when flushed)
        if (content) {
          listBuffer.push(content);
        }
      }
    } else {
      // regular line
      if (line.trim() !== "") {
        const cleaned = cleanText(line);
        if (cleaned) { // Only add if there's content after cleaning
          flushList();
          contentBuffer.push(
            <p key={"p" + contentBuffer.length} className="mb-2">
              {cleaned}
            </p>
          );
        }
      } else {
        // blank line – treat as separator
        flushList();
      }
    }
  });

  flushList();
  flushSection();
  
  return sections.length > 0 ? sections : null;
}

function App() {
  const initialSearchParams = new URLSearchParams(window.location.search);
  const hasRegistrationPaymentReturn = initialSearchParams.has('payment_session_id') || initialSearchParams.get('payment_canceled') === '1';
  const [selectedStock, setSelectedStock] = useState(null);
  const [stockData, setStockData] = useState(null);
  const [inputSymbol, setInputSymbol] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [userManagementAuthMode, setUserManagementAuthMode] = useState(
    initialSearchParams.get('auth') === 'register' || hasRegistrationPaymentReturn ? 'register' : 'login'
  );
  const [showAuthModal, setShowAuthModal] = useState(hasRegistrationPaymentReturn);
  const [authModalContext, setAuthModalContext] = useState('account');
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiAnalysisStructured, setAiAnalysisStructured] = useState(null);
  const [dayAiAnalysis, setDayAiAnalysis] = useState(null);
  const [dayAiAnalysisStructured, setDayAiAnalysisStructured] = useState(null);
  const [centimentAnalysis, setCentimentAnalysis] = useState(null);
  const [centimentAnalysisStructured, setCentimentAnalysisStructured] = useState(null);
  const [phaseOneAdvisors, setPhaseOneAdvisors] = useState(null);
  const [phaseOneAdvisorsLoading, setPhaseOneAdvisorsLoading] = useState(false);
  const [phaseTwoAdvisors, setPhaseTwoAdvisors] = useState(null);
  const [phaseTwoAdvisorsLoading, setPhaseTwoAdvisorsLoading] = useState(false);
  const [phaseThreeAdvisors, setPhaseThreeAdvisors] = useState(null);
  const [phaseThreeAdvisorsLoading, setPhaseThreeAdvisorsLoading] = useState(false);
  const [phaseOnePositionSizingLoading, setPhaseOnePositionSizingLoading] = useState(false);
  const [phaseOneCapital, setPhaseOneCapital] = useState('10000');
  const [phaseOneRiskPct, setPhaseOneRiskPct] = useState('1');
  const [phaseOneStopPct, setPhaseOneStopPct] = useState('3');
  const lastPositionSizingSignatureRef = useRef('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [historicalData, setHistoricalData] = useState(null);
  const [activeTab, setActiveTab] = useState("overview"); // "overview" or "ai-analysis" or "printable" or "company-news"
  const [activeButton, setActiveButton] = useState(null); // Track which button was clicked: "reports" or "ai" or "news" or "printable"
  const [companyNews, setCompanyNews] = useState(null);
  const [newsLoading, setNewsLoading] = useState(false);
  const [aiSubTab, setAiSubTab] = useState("sections"); // "sections" or "formatted"
  const [dayAiSubTab, setDayAiSubTab] = useState("sections"); // "sections" or "formatted"
  const [centimentSubTab, setCentimentSubTab] = useState("sections"); // "sections" or "formatted"
  const [darkTheme, setDarkTheme] = useState(false);
  const [showWorkspaceControls, setShowWorkspaceControls] = useState(true);

  useEffect(() => {
    const storedAuth = localStorage.getItem('stockAnalyzerAuth');
    if (!storedAuth) return;

    try {
      const parsed = JSON.parse(storedAuth);
      if (parsed?.token && parsed?.user) {
        (async () => {
          try {
            const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
              headers: { Authorization: `Bearer ${parsed.token}` }
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Session expired');
            setCurrentUser({ ...data.user, token: parsed.token });
          } catch (error) {
            localStorage.removeItem('stockAnalyzerAuth');
          }
        })();
      }
    } catch (error) {
      localStorage.removeItem('stockAnalyzerAuth');
    }
  }, []);

  const persistAuth = (user, token) => {
    const nextUser = { ...user, token };
    setCurrentUser(nextUser);
    localStorage.setItem('stockAnalyzerAuth', JSON.stringify({ user, token }));
  };

  const clearAuth = () => {
    setCurrentUser(null);
    localStorage.removeItem('stockAnalyzerAuth');
  };

  const authHeaders = React.useCallback(
    () => (currentUser?.token ? { Authorization: `Bearer ${currentUser.token}` } : {}),
    [currentUser?.token]
  );
  
  // Market movers state
  const [mostActive, setMostActive] = useState([]);
  const [topGainers, setTopGainers] = useState([]);
  const [topLosers, setTopLosers] = useState([]);
  const [showMostActive, setShowMostActive] = useState(false);
  const [showGainers, setShowGainers] = useState(false);
  const [showLosers, setShowLosers] = useState(false);
  const [preferredStocks, setPreferredStocks] = useState([]);
  const [hasFetchedMostActive, setHasFetchedMostActive] = useState(false);
  const [hasFetchedGainers, setHasFetchedGainers] = useState(false);
  const [hasFetchedLosers, setHasFetchedLosers] = useState(false);
  const [hasRequestedMostActive, setHasRequestedMostActive] = useState(false);
  const [hasRequestedGainers, setHasRequestedGainers] = useState(false);
  const [hasRequestedLosers, setHasRequestedLosers] = useState(false);
  const [marketRowLimit, setMarketRowLimit] = useState(25);
  const [activeLoading, setActiveLoading] = useState(false);
  const [gainersLoading, setGainersLoading] = useState(false);
  const [losersLoading, setLosersLoading] = useState(false);
  const [showPreferredStocks, setShowPreferredStocks] = useState(false);

  const extractMarketStocks = React.useCallback((payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.quotes)) return payload.quotes;
    const financeQuotes = payload.finance?.result?.[0]?.quotes;
    if (Array.isArray(financeQuotes)) return financeQuotes;
    return [];
  }, []);

  const fetchMostActive = async (forceRefresh = false) => {
    if (activeLoading) return;
    if (!forceRefresh && hasFetchedMostActive) return;
    
    setHasRequestedMostActive(true);
    setActiveLoading(true);
    const selectedLimit = MARKET_ROW_OPTIONS.includes(Number(marketRowLimit)) ? Number(marketRowLimit) : 25;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(`Request timed out after ${Math.round(MARKET_REQUEST_TIMEOUT_MS / 1000)} seconds`), MARKET_REQUEST_TIMEOUT_MS);
    try {
      console.log('Fetching most active stocks...');
      
      const response = await fetch(`${API_BASE_URL}/api/market/most-active?limit=${selectedLimit}`, {
        signal: controller.signal
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      let stocks = extractMarketStocks(data);

      if (!stocks.length) {
        const fallbackResponse = await fetch(`${API_BASE_URL}/api/most-active-stocks?limit=${selectedLimit}`, {
          signal: controller.signal
        });
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          stocks = extractMarketStocks(fallbackData);
        }
      }

      console.log('Most active stocks received:', stocks.length || 0);
      setMostActive(stocks);
      setHasFetchedMostActive(stocks.length > 0);
    } catch (err) {
      if (err?.name === 'AbortError') {
        console.warn('Most active request aborted:', err?.message || controller.signal?.reason || 'Request aborted');
      } else {
        console.error('Error fetching most active stocks:', err);
      }
    } finally {
      clearTimeout(timeoutId);
      setActiveLoading(false);
    }
  };

  const fetchGainers = async (forceRefresh = false) => {
    if (gainersLoading) return;
    if (!forceRefresh && hasFetchedGainers) return;
    
    setHasRequestedGainers(true);
    setGainersLoading(true);
    const selectedLimit = MARKET_ROW_OPTIONS.includes(Number(marketRowLimit)) ? Number(marketRowLimit) : 25;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(`Request timed out after ${Math.round(MARKET_REQUEST_TIMEOUT_MS / 1000)} seconds`), MARKET_REQUEST_TIMEOUT_MS);
    try {
      console.log('Fetching top gainers...');
      
      const response = await fetch(`${API_BASE_URL}/api/market/gainers?limit=${selectedLimit}`, {
        signal: controller.signal
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      let stocks = extractMarketStocks(data);

      if (!stocks.length) {
        const fallbackResponse = await fetch(`${API_BASE_URL}/api/day-gainers?limit=${selectedLimit}`, {
          signal: controller.signal
        });
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          stocks = extractMarketStocks(fallbackData);
        }
      }

      console.log('Top gainers received:', stocks.length || 0);
      setTopGainers(stocks);
      setHasFetchedGainers(stocks.length > 0);
    } catch (err) {
      if (err?.name === 'AbortError') {
        console.warn('Gainers request aborted:', err?.message || controller.signal?.reason || 'Request aborted');
      } else {
        console.error('Error fetching gainers:', err);
      }
    } finally {
      clearTimeout(timeoutId);
      setGainersLoading(false);
    }
  };

  const fetchLosers = async (forceRefresh = false) => {
    if (losersLoading) return;
    if (!forceRefresh && hasFetchedLosers) return;
    
    setHasRequestedLosers(true);
    setLosersLoading(true);
    const selectedLimit = MARKET_ROW_OPTIONS.includes(Number(marketRowLimit)) ? Number(marketRowLimit) : 25;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(`Request timed out after ${Math.round(MARKET_REQUEST_TIMEOUT_MS / 1000)} seconds`), MARKET_REQUEST_TIMEOUT_MS);
    try {
      console.log('Fetching top losers...');
      
      const response = await fetch(`${API_BASE_URL}/api/market/losers?limit=${selectedLimit}`, {
        signal: controller.signal
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      let stocks = extractMarketStocks(data);

      if (!stocks.length) {
        const fallbackResponse = await fetch(`${API_BASE_URL}/api/day-losers?limit=${selectedLimit}`, {
          signal: controller.signal
        });
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          stocks = extractMarketStocks(fallbackData);
        }
      }

      console.log('Top losers received:', stocks.length || 0);
      setTopLosers(stocks);
      setHasFetchedLosers(stocks.length > 0);
    } catch (err) {
      if (err?.name === 'AbortError') {
        console.warn('Losers request aborted:', err?.message || controller.signal?.reason || 'Request aborted');
      } else {
        console.error('Error fetching losers:', err);
      }
    } finally {
      clearTimeout(timeoutId);
      setLosersLoading(false);
    }
  };

  const updateCurrentUserPreferredStocks = React.useCallback(
    (nextPreferredStocks) => {
      const normalized = Array.from(new Set((nextPreferredStocks || []).map((symbol) => String(symbol).toUpperCase())));
      setPreferredStocks(normalized);
      setCurrentUser((prevUser) => {
        if (!prevUser?.token) return prevUser;

        const nextUser = { ...prevUser, preferred_stocks: normalized };
        const { token, ...userWithoutToken } = nextUser;
        localStorage.setItem('stockAnalyzerAuth', JSON.stringify({ user: userWithoutToken, token }));
        return nextUser;
      });
    },
    []
  );

  const fetchPreferredStocks = React.useCallback(async () => {
    if (!currentUser?.token || !currentUser?.is_approved) {
      setPreferredStocks([]);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/preferred-stocks`, {
        headers: { ...authHeaders() }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load preferred stocks');
      updateCurrentUserPreferredStocks(data.preferred_stocks || []);
    } catch (err) {
      console.error('Error fetching preferred stocks:', err);
    }
  }, [authHeaders, currentUser?.is_approved, currentUser?.token, updateCurrentUserPreferredStocks]);

  useEffect(() => {
    if (!currentUser?.token) {
      setPreferredStocks([]);
      return;
    }

    setPreferredStocks(currentUser.preferred_stocks || []);
  }, [currentUser?.preferred_stocks, currentUser?.token]);

  // Fetch market movers on component mount
  React.useEffect(() => {
    // Don't fetch on mount - fetch on demand when user clicks buttons
  }, []);

  const refreshVisibleMarketData = () => {
    if (showMostActive) {
      fetchMostActive(true);
      return;
    }

    if (showGainers) {
      fetchGainers(true);
      return;
    }

    if (showLosers) {
      fetchLosers(true);
      return;
    }

    if (showPreferredStocks) {
      fetchPreferredStocks();
      return;
    }
  };

  // const stocks = ["AAPL", "GOOG", "MSFT", "AMZN", "TSLA"];

  const fetchCompanyNews = async (symbol) => {
    setNewsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/stock/news/${symbol}`);
      const newsText = await response.text();
      const newsData = JSON.parse(newsText.replace(/:\s*NaN/g, ': null'));
      
      if (newsData.error) throw new Error(newsData.error);
      
      setCompanyNews(newsData);
      console.log('Company News:', newsData);
    } catch (err) {
      console.error("Error fetching news:", err);
      setCompanyNews({ error: err.message, news: [] });
    } finally {
      setNewsLoading(false);
    }
  };

  const clearAdvisorResults = () => {
    setAiAnalysis(null);
    setAiAnalysisStructured(null);
    setDayAiAnalysis(null);
    setDayAiAnalysisStructured(null);
    setCentimentAnalysis(null);
    setCentimentAnalysisStructured(null);
    setPhaseOneAdvisors(null);
    setPhaseTwoAdvisors(null);
    setPhaseThreeAdvisors(null);
  };

  const resetForSymbolChange = (symbol) => {
    const normalizedSymbol = String(symbol || '').trim().toUpperCase();
    const changed = selectedStock !== normalizedSymbol;
    setSelectedStock(normalizedSymbol);

    if (changed) {
      setStockData(null);
      setHistoricalData(null);
      setCompanyNews(null);
      clearAdvisorResults();
      setError(null);
    }

    return changed;
  };

  const fetchStockDetails = async (symbol) => {
    const normalizedSymbol = String(symbol || '').trim().toUpperCase();
    resetForSymbolChange(normalizedSymbol);
    setError(null);
    setIsLoading(true);
    setActiveButton('ai'); // Mark AI button as active
    setActiveTab('ai-analysis'); // Switch to AI analysis tab

    try {
      // Fetch stock data, AI analysis, and historical data in parallel
      const [stockResponse, analysisResponse, historicalResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/stock/${normalizedSymbol}`),
        fetch(`${API_BASE_URL}/api/stock/analysisformated/${normalizedSymbol}`, { headers: { ...authHeaders() } }),
        fetch(`${API_BASE_URL}/api/stock/historical/${normalizedSymbol}`)
      ]);

      // Get response text first to handle NaN values
      const stockText = await stockResponse.text();
      const analysisText = await analysisResponse.text();
      const historicalText = await historicalResponse.text();

      // Replace NaN with null before parsing
      const stockData = JSON.parse(stockText.replace(/:\s*NaN/g, ': null'));
      const analysisData = JSON.parse(analysisText.replace(/:\s*NaN/g, ': null'));
      const historicalData = JSON.parse(historicalText.replace(/:\s*NaN/g, ': null'));

      if (stockData.error) throw new Error(stockData.error);
      if (analysisData.error) throw new Error(analysisData.error);
      if (historicalData.error) throw new Error(historicalData.error);

      setStockData(stockData);
      setAiAnalysis(analysisData.raw_analysis);
      setAiAnalysisStructured(analysisData.analysis);
      setHistoricalData(historicalData);
      
      // Debug: Check if chart data is present
      console.log('Historical Data:', historicalData);
      console.log('Chart Data (30 days):', historicalData.chart_data);
      console.log('Chart Data (1 year):', historicalData.chart_data_year);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDayTradingDetails = async (symbol) => {
    const normalizedSymbol = String(symbol || '').trim().toUpperCase();
    resetForSymbolChange(normalizedSymbol);
    setError(null);
    setIsLoading(true);
    setActiveButton('day-ai');
    setActiveTab('day-ai-analysis');

    try {
      const [stockResponse, analysisResponse, historicalResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/stock/${normalizedSymbol}`),
        fetch(`${API_BASE_URL}/api/stock/day-analysisformated/${normalizedSymbol}`, { headers: { ...authHeaders() } }),
        fetch(`${API_BASE_URL}/api/stock/historical/${normalizedSymbol}`)
      ]);

      const stockText = await stockResponse.text();
      const analysisText = await analysisResponse.text();
      const historicalText = await historicalResponse.text();

      const stockData = JSON.parse(stockText.replace(/:\s*NaN/g, ': null'));
      const analysisData = JSON.parse(analysisText.replace(/:\s*NaN/g, ': null'));
      const historicalData = JSON.parse(historicalText.replace(/:\s*NaN/g, ': null'));

      if (stockData.error) throw new Error(stockData.error);
      if (analysisData.error) throw new Error(analysisData.error);
      if (historicalData.error) throw new Error(historicalData.error);

      setStockData(stockData);
      setDayAiAnalysis(analysisData.raw_analysis);
      setDayAiAnalysisStructured(analysisData.analysis);
      setHistoricalData(historicalData);
    } catch (err) {
      console.error("Error fetching day-trading data:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReportsOnly = async (symbol) => {
    const normalizedSymbol = String(symbol || '').trim().toUpperCase();
    resetForSymbolChange(normalizedSymbol);
    setError(null);
    setIsLoading(true);
    setActiveButton('overview'); // Mark Overview button as active
    setActiveTab('overview'); // Switch to overview tab

    try {
      // Fetch only stock data and historical data (no AI analysis)
      const [stockResponse, historicalResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/stock/${normalizedSymbol}`),
        fetch(`${API_BASE_URL}/api/stock/historical/${normalizedSymbol}`)
      ]);

      // Get response text first to handle NaN values
      const stockText = await stockResponse.text();
      const historicalText = await historicalResponse.text();

      // Replace NaN with null before parsing
      const stockData = JSON.parse(stockText.replace(/:\s*NaN/g, ': null'));
      const historicalData = JSON.parse(historicalText.replace(/:\s*NaN/g, ': null'));

      if (stockData.error) throw new Error(stockData.error);
      if (historicalData.error) throw new Error(historicalData.error);

      setStockData(stockData);
      setHistoricalData(historicalData);
      
      // Debug: Check if chart data is present
      console.log('Historical Data:', historicalData);
      console.log('Chart Data (30 days):', historicalData.chart_data);
      console.log('Chart Data (1 year):', historicalData.chart_data_year);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCentimentDetails = async (symbol) => {
    const normalizedSymbol = String(symbol || '').trim().toUpperCase();
    resetForSymbolChange(normalizedSymbol);
    setError(null);
    setIsLoading(true);
    setActiveButton('centiment-ai');
    setActiveTab('centiment-ai-analysis');

    try {
      const [stockResponse, analysisResponse, historicalResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/stock/${normalizedSymbol}`),
        fetch(`${API_BASE_URL}/api/stock/centiment-analysisformated/${normalizedSymbol}`, { headers: { ...authHeaders() } }),
        fetch(`${API_BASE_URL}/api/stock/historical/${normalizedSymbol}`)
      ]);

      const stockText = await stockResponse.text();
      const analysisText = await analysisResponse.text();
      const historicalText = await historicalResponse.text();

      const stockData = JSON.parse(stockText.replace(/:\s*NaN/g, ': null'));
      const analysisData = JSON.parse(analysisText.replace(/:\s*NaN/g, ': null'));
      const historicalData = JSON.parse(historicalText.replace(/:\s*NaN/g, ': null'));

      if (stockData.error) throw new Error(stockData.error);
      if (analysisData.error) throw new Error(analysisData.error);
      if (historicalData.error) throw new Error(historicalData.error);

      setStockData(stockData);
      setCentimentAnalysis(analysisData.raw_analysis);
      setCentimentAnalysisStructured(analysisData.analysis);
      setHistoricalData(historicalData);
    } catch (err) {
      console.error("Error fetching centiment data:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPhaseOneAdvisors = async (symbol) => {
    const normalizedSymbol = String(symbol || '').trim().toUpperCase();
    const symbolChanged = resetForSymbolChange(normalizedSymbol);
    if (symbolChanged) {
      setPhaseOneAdvisors(null);
    }
    setError(null);
    setIsLoading(true);
    setPhaseOneAdvisorsLoading(true);
    setActiveButton('phase1-advisors');
    setActiveTab('phase1-advisors');

    try {
      const positionSizingParams = new URLSearchParams({
        capital: phaseOneCapital || '10000',
        risk_pct: phaseOneRiskPct || '1',
        stop_pct: phaseOneStopPct || '3'
      });
      const signature = `${normalizedSymbol}|${positionSizingParams.toString()}`;

      const [
        stockResponse,
        historicalResponse,
        positionSizingResponse,
        stopLossResponse,
        entryExitResponse
      ] = await Promise.all([
        fetch(`${API_BASE_URL}/api/stock/${normalizedSymbol}`),
        fetch(`${API_BASE_URL}/api/stock/historical/${normalizedSymbol}`),
        fetch(`${API_BASE_URL}/api/stock/position-sizing/${normalizedSymbol}?${positionSizingParams.toString()}`, { headers: { ...authHeaders() } }),
        fetch(`${API_BASE_URL}/api/stock/stoploss/${normalizedSymbol}`, { headers: { ...authHeaders() } }),
        fetch(`${API_BASE_URL}/api/stock/entry-exit/${normalizedSymbol}`, { headers: { ...authHeaders() } })
      ]);

      const stockText = await stockResponse.text();
      const historicalText = await historicalResponse.text();
      const positionSizingText = await positionSizingResponse.text();
      const stopLossText = await stopLossResponse.text();
      const entryExitText = await entryExitResponse.text();

      const stockPayload = JSON.parse(stockText.replace(/:\s*NaN/g, ': null'));
      const historicalPayload = JSON.parse(historicalText.replace(/:\s*NaN/g, ': null'));
      const positionSizingPayload = JSON.parse(positionSizingText.replace(/:\s*NaN/g, ': null'));
      const stopLossPayload = JSON.parse(stopLossText.replace(/:\s*NaN/g, ': null'));
      const entryExitPayload = JSON.parse(entryExitText.replace(/:\s*NaN/g, ': null'));

      if (stockPayload.error) throw new Error(stockPayload.error);
      if (historicalPayload.error) throw new Error(historicalPayload.error);
      if (positionSizingPayload.error) throw new Error(positionSizingPayload.error);
      if (stopLossPayload.error) throw new Error(stopLossPayload.error);
      if (entryExitPayload.error) throw new Error(entryExitPayload.error);

      setStockData(stockPayload);
      setHistoricalData(historicalPayload);
      setPhaseOneAdvisors({
        positionSizing: positionSizingPayload,
        stopLoss: stopLossPayload,
        entryExit: entryExitPayload
      });
      lastPositionSizingSignatureRef.current = signature;
    } catch (err) {
      console.error("Error fetching trade setup tools:", err);
      setError(err.message || 'Failed to fetch trade setup tools');
    } finally {
      setIsLoading(false);
      setPhaseOneAdvisorsLoading(false);
    }
  };

  const fetchPhaseOnePositionSizingOnly = useCallback(async (symbol, force = false) => {
    if (!symbol || !currentUser?.token) return;

    const positionSizingParams = new URLSearchParams({
      capital: phaseOneCapital || '10000',
      risk_pct: phaseOneRiskPct || '1',
      stop_pct: phaseOneStopPct || '3'
    });
    const signature = `${symbol.toUpperCase()}|${positionSizingParams.toString()}`;

    if (!force && signature === lastPositionSizingSignatureRef.current) {
      return;
    }

    setPhaseOnePositionSizingLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/stock/position-sizing/${symbol}?${positionSizingParams.toString()}`,
        { headers: { ...authHeaders() } }
      );
      const text = await response.text();
      const payload = JSON.parse(text.replace(/:\s*NaN/g, ': null'));
      if (payload.error) throw new Error(payload.error);

      setPhaseOneAdvisors(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          positionSizing: payload
        };
      });
      lastPositionSizingSignatureRef.current = signature;
    } catch (err) {
      console.error('Error recalculating position sizing:', err);
      setError(err.message || 'Failed to recalculate position sizing');
    } finally {
      setPhaseOnePositionSizingLoading(false);
    }
  }, [authHeaders, currentUser?.token, phaseOneCapital, phaseOneRiskPct, phaseOneStopPct]);

  useEffect(() => {
    if (!phaseOneAdvisors) return;
    if (activeTab !== 'phase1-advisors') return;
    const symbol = (selectedStock || inputSymbol || '').trim().toUpperCase();
    if (!symbol || !currentUser?.token) return;

    const timer = setTimeout(() => {
      fetchPhaseOnePositionSizingOnly(symbol);
    }, 450);

    return () => clearTimeout(timer);
  }, [
    phaseOneCapital,
    phaseOneRiskPct,
    phaseOneStopPct,
    activeTab,
    selectedStock,
    inputSymbol,
    currentUser?.token,
    phaseOneAdvisors,
    fetchPhaseOnePositionSizingOnly
  ]);

  const fetchPhaseTwoAdvisors = async (symbol) => {
    const normalizedSymbol = String(symbol || '').trim().toUpperCase();
    const symbolChanged = resetForSymbolChange(normalizedSymbol);
    if (symbolChanged) {
      setPhaseTwoAdvisors(null);
    }
    setError(null);
    setIsLoading(true);
    setPhaseTwoAdvisorsLoading(true);
    setActiveButton('phase2-advisors');
    setActiveTab('phase2-advisors');

    try {
      const [
        stockResponse,
        historicalResponse,
        regimeResponse,
        gapMomentumResponse,
        sectorRotationResponse
      ] = await Promise.all([
        fetch(`${API_BASE_URL}/api/stock/${normalizedSymbol}`),
        fetch(`${API_BASE_URL}/api/stock/historical/${normalizedSymbol}`),
        fetch(`${API_BASE_URL}/api/market/regime`, { headers: { ...authHeaders() } }),
        fetch(`${API_BASE_URL}/api/stock/gap-momentum/${normalizedSymbol}`, { headers: { ...authHeaders() } }),
        fetch(`${API_BASE_URL}/api/market/sector-rotation`, { headers: { ...authHeaders() } })
      ]);

      const stockText = await stockResponse.text();
      const historicalText = await historicalResponse.text();
      const regimeText = await regimeResponse.text();
      const gapMomentumText = await gapMomentumResponse.text();
      const sectorRotationText = await sectorRotationResponse.text();

      const stockPayload = JSON.parse(stockText.replace(/:\s*NaN/g, ': null'));
      const historicalPayload = JSON.parse(historicalText.replace(/:\s*NaN/g, ': null'));
      const regimePayload = JSON.parse(regimeText.replace(/:\s*NaN/g, ': null'));
      const gapMomentumPayload = JSON.parse(gapMomentumText.replace(/:\s*NaN/g, ': null'));
      const sectorRotationPayload = JSON.parse(sectorRotationText.replace(/:\s*NaN/g, ': null'));

      if (stockPayload.error) throw new Error(stockPayload.error);
      if (historicalPayload.error) throw new Error(historicalPayload.error);
      if (regimePayload.error) throw new Error(regimePayload.error);
      if (gapMomentumPayload.error) throw new Error(gapMomentumPayload.error);
      if (sectorRotationPayload.error) throw new Error(sectorRotationPayload.error);

      setStockData(stockPayload);
      setHistoricalData(historicalPayload);
      setPhaseTwoAdvisors({
        regime: regimePayload,
        gapMomentum: gapMomentumPayload,
        sectorRotation: sectorRotationPayload
      });
    } catch (err) {
      console.error("Error fetching Regime, Momentum & Rotation:", err);
      setError(err.message || 'Failed to fetch Regime, Momentum & Rotation');
    } finally {
      setIsLoading(false);
      setPhaseTwoAdvisorsLoading(false);
    }
  };

  const fetchPhaseThreeAdvisors = async (symbol) => {
    const normalizedSymbol = String(symbol || '').trim().toUpperCase();
    const symbolChanged = resetForSymbolChange(normalizedSymbol);
    if (symbolChanged) {
      setPhaseThreeAdvisors(null);
    }
    setError(null);
    setIsLoading(true);
    setPhaseThreeAdvisorsLoading(true);
    setActiveButton('phase3-advisors');
    setActiveTab('phase3-advisors');

    try {
      const [
        stockResponse,
        historicalResponse,
        earningsResponse,
        portfolioResponse,
        consensusResponse,
        optionsResponse
      ] = await Promise.all([
        fetch(`${API_BASE_URL}/api/stock/${normalizedSymbol}`),
        fetch(`${API_BASE_URL}/api/stock/historical/${normalizedSymbol}`),
        fetch(`${API_BASE_URL}/api/stock/earnings-risk/${normalizedSymbol}`, { headers: { ...authHeaders() } }),
        fetch(`${API_BASE_URL}/api/portfolio/risk-analysis`, { headers: { ...authHeaders() } }),
        fetch(`${API_BASE_URL}/api/stock/advisor-consensus/${normalizedSymbol}`, { headers: { ...authHeaders() } }),
        fetch(`${API_BASE_URL}/api/stock/options-strategy/${normalizedSymbol}?outlook=neutral`, { headers: { ...authHeaders() } })
      ]);

      const stockText = await stockResponse.text();
      const historicalText = await historicalResponse.text();
      const earningsText = await earningsResponse.text();
      const portfolioText = await portfolioResponse.text();
      const consensusText = await consensusResponse.text();
      const optionsText = await optionsResponse.text();

      const stockPayload = JSON.parse(stockText.replace(/:\s*NaN/g, ': null'));
      const historicalPayload = JSON.parse(historicalText.replace(/:\s*NaN/g, ': null'));
      const earningsPayload = JSON.parse(earningsText.replace(/:\s*NaN/g, ': null'));
      const portfolioPayload = JSON.parse(portfolioText.replace(/:\s*NaN/g, ': null'));
      const consensusPayload = JSON.parse(consensusText.replace(/:\s*NaN/g, ': null'));
      const optionsPayload = JSON.parse(optionsText.replace(/:\s*NaN/g, ': null'));

      if (stockPayload.error) throw new Error(stockPayload.error);
      if (historicalPayload.error) throw new Error(historicalPayload.error);
      if (earningsPayload.error) throw new Error(earningsPayload.error);
      if (portfolioPayload.error) throw new Error(portfolioPayload.error);
      if (consensusPayload.error) throw new Error(consensusPayload.error);
      if (optionsPayload.error) throw new Error(optionsPayload.error);

      setStockData(stockPayload);
      setHistoricalData(historicalPayload);
      setPhaseThreeAdvisors({
        earnings: earningsPayload,
        portfolio: portfolioPayload,
        consensus: consensusPayload,
        options: optionsPayload
      });
    } catch (err) {
      console.error("Error fetching Risk Intelligence & Options Strategy:", err);
      setError(err.message || 'Failed to fetch Risk Intelligence & Options Strategy');
    } finally {
      setIsLoading(false);
      setPhaseThreeAdvisorsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputSymbol.trim()) {
      fetchReportsOnly(inputSymbol.trim().toUpperCase());
    }
  };

  // Overview is always public (no login required)
  const handleStockOverview = () => {
    const symbol = inputSymbol.trim().toUpperCase();
    if (symbol) {
      if (!stockData || selectedStock !== symbol) {
        setActiveButton('overview');
        fetchReportsOnly(symbol);
      } else {
        setActiveButton('overview');
        setActiveTab('overview');
      }
    }
  };

  // Charts is always public (no login required)
  const handleGetCharts = () => {
    const symbol = inputSymbol.trim().toUpperCase();
    if (symbol) {
      if (!stockData || selectedStock !== symbol) {
        setActiveButton('reports');
        resetForSymbolChange(symbol);
        setError(null);
        setIsLoading(true);

        (async () => {
          try {
            const [stockResponse, historicalResponse] = await Promise.all([
              fetch(`${API_BASE_URL}/api/stock/${symbol}`),
              fetch(`${API_BASE_URL}/api/stock/historical/${symbol}`)
            ]);

            const stockText = await stockResponse.text();
            const historicalText = await historicalResponse.text();

            const stockData = JSON.parse(stockText.replace(/:\s*NaN/g, ': null'));
            const historicalData = JSON.parse(historicalText.replace(/:\s*NaN/g, ': null'));

            if (stockData.error) throw new Error(stockData.error);
            if (historicalData.error) throw new Error(historicalData.error);

            setStockData(stockData);
            setHistoricalData(historicalData);
            setActiveTab('charts');
          } catch (err) {
            setError(err.message || 'Failed to fetch stock data');
          } finally {
            setIsLoading(false);
          }
        })();
      } else {
        setActiveButton('reports');
        setActiveTab('charts');
      }
    }
  };

  const handleAnalyzeWithAI = () => {
    const symbol = inputSymbol.trim().toUpperCase();
    if (symbol) {
      if (!currentUser?.token) {
        setError('Please sign in to use Swing Trade Advisor.');
        setActiveTab('account');
        return;
      }
      // Always fetch for AI analysis since we need the analysisformated endpoint
      fetchStockDetails(symbol);
    }
  };

  const handleAnalyzeDayTrading = () => {
    const symbol = inputSymbol.trim().toUpperCase();
    if (symbol) {
      if (!currentUser?.token) {
        setError('Please sign in to use Day Trade Advisor.');
        setActiveTab('account');
        return;
      }
      fetchDayTradingDetails(symbol);
    }
  };

  const handleAnalyzeCentiment = () => {
    const symbol = inputSymbol.trim().toUpperCase();
    if (symbol) {
      if (!currentUser?.token) {
        setError('Please sign in to use Centiment Advisor.');
        setActiveTab('account');
        return;
      }
      fetchCentimentDetails(symbol);
    }
  };

  const handlePhaseOneAdvisors = () => {
    const symbol = inputSymbol.trim().toUpperCase();
    if (symbol) {
      if (!currentUser?.token) {
        setError('Please sign in to use Trade Setup Tools.');
        setActiveTab('account');
        return;
      }
      fetchPhaseOneAdvisors(symbol);
    }
  };

  const handleRecalculatePhaseOne = () => {
    const symbol = (selectedStock || inputSymbol || '').trim().toUpperCase();
    if (!symbol) {
      setError('Select a stock symbol to recalculate Phase 1 advisor values.');
      return;
    }
    if (!currentUser?.token) {
      setError('Please sign in to use Trade Setup Tools.');
      setActiveTab('account');
      return;
    }
    fetchPhaseOnePositionSizingOnly(symbol, true);
  };

  const handlePhaseTwoAdvisors = () => {
    const symbol = inputSymbol.trim().toUpperCase();
    if (symbol) {
      if (!currentUser?.token) {
        setError('Please sign in to use Regime, Momentum & Rotation.');
        setActiveTab('account');
        return;
      }
      fetchPhaseTwoAdvisors(symbol);
    }
  };

  const handlePhaseThreeAdvisors = () => {
    const symbol = inputSymbol.trim().toUpperCase();
    if (symbol) {
      if (!currentUser?.token) {
        setError('Please sign in to use Risk Intelligence & Options Strategy.');
        setActiveTab('account');
        return;
      }
      fetchPhaseThreeAdvisors(symbol);
    }
  };

  const handleOpenUserManagement = () => {
    setActiveTab('user-management');
    setActiveButton('user-management');
  };

  const handleOpenAccountScreen = () => {
    setActiveTab('account');
    setActiveButton('account');
  };

  const handleOpenAuthModal = (authMode = 'login', context = 'account') => {
    setUserManagementAuthMode(authMode === 'register' ? 'register' : 'login');
    setAuthModalContext(context === 'admin' ? 'admin' : 'account');
    setShowAuthModal(true);
  };

  const handleOpenAdminAccess = () => {
    if (currentUser?.role === 'admin') {
      handleOpenUserManagement();
      return;
    }
    handleOpenAuthModal('login', 'admin');
  };

  // News is always public (no login required)
  const handleGetNews = () => {
    const symbol = inputSymbol.trim().toUpperCase();
    if (symbol) {
      if (!companyNews || selectedStock !== symbol) {
        setSelectedStock(symbol);
        setNewsLoading(true);
        setCompanyNews(null);
        setActiveButton('news');
        setActiveTab('company-news');
        fetchCompanyNews(symbol);
      } else {
        setActiveButton('news');
        setActiveTab('company-news');
      }
    }
  };

  const handleShowReports = () => {
    if (selectedStock) {
      setActiveButton('printable');
      setActiveTab('printable'); // Switch to Printable Report tab
    }
  };

  const actionButtonSizing = {
    minHeight: '55px',
    minWidth: '95px',
    maxWidth: '95px',
    flex: '0 0 95px'
  };
  const isGuestUser = !currentUser?.token;

  const handlePrint = () => {
    // Format date and time using local timezone
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const date = `${year}-${month}-${day}`; // YYYY-MM-DD
    const time = now.toTimeString().split(' ')[0].replace(/:/g, '-'); // HH-MM-SS
    
    // Create filename: TickerName-Date-Time-ATS-Stocks-TradeAdvisor-AI.pdf
    const filename = `${selectedStock}-${date}-${time}-ATS-Stocks-TradeAdvisor-AI.pdf`;
    
    // Set document title (this becomes the default filename in print dialog)
    const originalTitle = document.title;
    document.title = filename.replace('.pdf', '');
    
    // Trigger print
    window.print();
    
    // Restore original title after a short delay
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const loggedInUserLabel =
    currentUser?.name ||
    currentUser?.full_name ||
    currentUser?.username ||
    currentUser?.email ||
    currentUser?.id ||
    null;

  return (
    <div className="container-fluid py-2 py-md-4 px-0 px-md-3" style={{minHeight:'100vh', backgroundColor: darkTheme ? '#1e293b' : '#f8f9fa', overflowX:'hidden', maxWidth:'100vw', transition: 'background-color 0.3s ease'}}>
      <div className="p-3 p-md-4 mb-2 mx-2 mx-md-0 d-flex justify-content-between align-items-center" style={{
        background: darkTheme ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)' : 'linear-gradient(135deg, #0f172a 0%, #1e40af 50%, #3b82f6 100%)',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h1 className="h4 h2-md m-0">
          <span style={{fontWeight: '700', fontSize: '1.5rem', letterSpacing: '1px', color: '#e0f2fe'}}>ATS-Stocks</span>
          <span style={{margin: '0 0.3rem', color: '#e0f2fe'}}>-</span>
          <span style={{fontWeight: '700', fontSize: '1.5rem', letterSpacing: '1px', color: darkTheme ? '#c084fc' : '#ffffff'}}>TradeAdvisor AI</span>
        </h1>
        <div className="d-flex align-items-center gap-2 d-print-none">
          {!currentUser?.token && (
            <>
              <button
                onClick={() => handleOpenAuthModal('register', 'account')}
                className="btn btn-sm"
                style={{
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  color: '#fff',
                  padding: '0.3rem 0.65rem',
                  borderRadius: '6px',
                  fontSize: '0.82rem',
                  fontWeight: 600
                }}
              >
                Register
              </button>
              <button
                onClick={() => handleOpenAuthModal('login', 'account')}
                className="btn btn-sm"
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  color: '#e0f2fe',
                  padding: '0.3rem 0.65rem',
                  borderRadius: '6px',
                  fontSize: '0.82rem',
                  fontWeight: 600
                }}
              >
                Sign In
              </button>
            </>
          )}
          {currentUser?.token && (
            <button
              onClick={clearAuth}
              className="btn btn-sm"
              style={{
                background: 'rgba(248, 113, 113, 0.16)',
                border: '1px solid rgba(248, 113, 113, 0.55)',
                color: '#fee2e2',
                padding: '0.3rem 0.65rem',
                borderRadius: '6px',
                fontSize: '0.82rem',
                fontWeight: 600
              }}
            >
              Sign Out
            </button>
          )}
          {loggedInUserLabel && (
            <span
              style={{
                background: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.35)',
                color: '#e0f2fe',
                padding: '0.25rem 0.55rem',
                borderRadius: '999px',
                fontSize: '0.78rem',
                fontWeight: 600,
                maxWidth: '240px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
              title={`Logged in as ${loggedInUserLabel}`}
            >
              Signed in: {loggedInUserLabel}
            </span>
          )}

          <button 
            onClick={() => setDarkTheme(!darkTheme)}
            className="btn btn-sm"
            style={{
              background: darkTheme ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' : 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              border: 'none',
              color: '#fff',
              padding: '0.3rem 0.6rem',
              borderRadius: '6px',
              fontSize: '0.9rem',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              transition: 'all 0.3s ease'
            }}
            title={darkTheme ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {darkTheme ? '☀️' : '✨'}
          </button>
        </div>
      </div>

      {showAuthModal && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-2 p-md-4"
          style={{ backgroundColor: 'rgba(2, 6, 23, 0.72)', zIndex: 3000 }}
        >
          <div
            className="w-100 rounded-3"
            style={{
              maxWidth: '1280px',
              maxHeight: '92vh',
              overflowY: 'auto',
              background: darkTheme ? '#0b1220' : '#f8fafc',
              border: darkTheme ? '1px solid #334155' : '1px solid #cbd5e1',
              boxShadow: '0 18px 45px rgba(0, 0, 0, 0.35)'
            }}
          >
            <div className="d-flex justify-content-between align-items-center px-3 px-md-4 pt-3 pb-2">
              <h5 className="mb-0" style={{ color: darkTheme ? '#e2e8f0' : '#0f172a' }}>
                {authModalContext === 'admin' ? 'Admin Login' : 'Account Access'}
              </h5>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setShowAuthModal(false)}
              >
                Close
              </button>
            </div>
            <UserManagement
              apiBaseUrl={API_BASE_URL}
              currentUser={authModalContext === 'admin' ? null : currentUser}
              screen="account"
              compactLogin={authModalContext === 'admin'}
              requestedAuthMode={authModalContext === 'admin' ? 'login' : userManagementAuthMode}
              onAuthChange={(userWithToken) => {
                persistAuth(userWithToken, userWithToken.token);
                setShowAuthModal(false);
              }}
              onLogout={clearAuth}
              onPreferredStocksChange={updateCurrentUserPreferredStocks}
              onSelectStock={(symbol) => setInputSymbol(symbol)}
              darkTheme={darkTheme}
            />
          </div>
        </div>
      )}

  <div className="row g-2 g-md-4 mx-0" style={{maxWidth:'100%'}}>
        {/* Left Column - full width on mobile, sidebar on desktop */}
  <div className="col-12 d-print-none px-2 px-md-3">
          {/* Stock Symbol Input Form */}
          <form onSubmit={handleSubmit} className="mb-2">
            <div className="p-2 p-md-3 border rounded shadow-sm w-100" style={{
              backgroundColor: darkTheme ? '#334155' : '#fff',
              borderColor: darkTheme ? '#475569' : '#dee2e6',
              transition: 'background-color 0.3s ease'
            }}>
              <div className="d-flex justify-content-end mb-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    const nextVisible = !showWorkspaceControls;
                    setShowWorkspaceControls(nextVisible);
                    if (!nextVisible) {
                      setShowMostActive(false);
                      setShowGainers(false);
                      setShowLosers(false);
                      setShowPreferredStocks(false);
                    }
                  }}
                >
                  {showWorkspaceControls ? 'Hide Select Stock & Actions' : 'Show Select Stock & Actions'}
                </button>
              </div>

              {showWorkspaceControls && (
              <div className="primary-sections-layout d-flex flex-column flex-lg-row">
                {/* Stock Selection Section */}
                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                  <div className="mb-2 pb-2" style={{borderBottom: darkTheme ? '1px solid #475569' : '1px solid #dee2e6'}}>
                    <h6 className="mb-0 d-flex align-items-center flex-wrap" style={{fontSize: '0.85rem', fontWeight: '600', color: darkTheme ? '#94a3b8' : '#6c757d'}}>
                      <span>📈 Select Stock</span>
                      <span className="ms-2" style={{fontSize: '0.68rem', fontWeight: 400, lineHeight: 1.2, whiteSpace: 'normal', color: darkTheme ? '#64748b' : '#6c757d'}}>
                        ( Use buttons below to select or type in the box )
                      </span>
                    </h6>
                  </div>
                  {/* Market Movers and Stock Symbol Input Grouped */}
                  <div>
                    <div className="position-relative mb-2">
                      <div className="d-flex flex-wrap gap-2 mb-2 align-items-end">
                        <button
                          type="button"
                          onClick={() => {
                            if (!showMostActive && (!hasFetchedMostActive || mostActive.length === 0)) {
                              fetchMostActive();
                            }
                            setShowMostActive(!showMostActive);
                            setShowGainers(false);
                            setShowLosers(false);
                            setShowPreferredStocks(false);
                          }}
                          className="btn btn-sm"
                          style={{
                            background: showMostActive 
                              ? 'linear-gradient(90deg,#fbbf24,#f59e42 80%)' 
                              : '#fef9c3',
                            border: showMostActive ? '2px solid #f59e42' : '1px solid #fde68a',
                            color: showMostActive ? '#fff' : '#b45309',
                            padding: '0.4rem 0.8rem',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            minHeight: '38px',
                            boxShadow: showMostActive ? '0 2px 8px rgba(245,158,66,0.18)' : 'none'
                          }}
                        >
                          ⚡Active
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (!showGainers && (!hasFetchedGainers || topGainers.length === 0)) {
                              fetchGainers();
                            }
                            setShowGainers(!showGainers);
                            setShowMostActive(false);
                            setShowLosers(false);
                            setShowPreferredStocks(false);
                          }}
                          className="btn btn-sm"
                          style={{
                            background: showGainers 
                              ? 'linear-gradient(90deg,#0d9488,#0f766e)' 
                              : 'linear-gradient(90deg,#14b8a6,#0d9488)',
                            border: showGainers ? '2px solid #0f766e' : '1px solid #0d9488',
                            color: '#fff',
                            padding: '0.4rem 0.8rem',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            minHeight: '38px',
                            boxShadow: showGainers ? '0 2px 4px rgba(0,0,0,0.2)' : 'none'
                          }}
                        >
                          📈Gainers
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (!showLosers && (!hasFetchedLosers || topLosers.length === 0)) {
                              fetchLosers();
                            }
                            setShowLosers(!showLosers);
                            setShowMostActive(false);
                            setShowGainers(false);
                            setShowPreferredStocks(false);
                          }}
                          className="btn btn-sm"
                          style={{
                            background: showLosers 
                              ? 'linear-gradient(90deg,#e11d48,#be123c)' 
                              : 'linear-gradient(90deg,#f43f5e,#e11d48)',
                            border: showLosers ? '2px solid #be123c' : '1px solid #e11d48',
                            color: '#fff',
                            padding: '0.4rem 0.8rem',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            minHeight: '38px',
                            boxShadow: showLosers ? '0 2px 4px rgba(0,0,0,0.2)' : 'none'
                          }}
                        >
                          📉Losers
                        </button>
                        {currentUser && (
                          <button
                            type="button"
                            onClick={() => {
                              if (!showPreferredStocks) {
                                fetchPreferredStocks();
                              }
                              setShowPreferredStocks(!showPreferredStocks);
                              setShowMostActive(false);
                              setShowGainers(false);
                              setShowLosers(false);
                            }}
                            className="btn btn-sm"
                            disabled={!currentUser?.is_approved}
                            style={{
                              background: showPreferredStocks 
                                ? 'linear-gradient(90deg,#7c3aed,#6d28d9)' 
                                : 'linear-gradient(90deg,#a78bfa,#7c3aed)',
                              border: showPreferredStocks ? '2px solid #6d28d9' : '1px solid #7c3aed',
                              color: '#fff',
                              padding: '0.4rem 0.8rem',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              minHeight: '38px',
                              boxShadow: showPreferredStocks ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
                              opacity: currentUser?.is_approved ? 1 : 0.6,
                              cursor: currentUser?.is_approved ? 'pointer' : 'not-allowed'
                            }}
                            title={currentUser?.is_approved ? 'Show preferred stocks' : 'Preferred stocks unlock after admin approval'}
                          >
                            ⭐Preferred
                          </button>
                        )}
                        <div className="ms-1" style={{ minWidth: '70px' }}>
                          <label htmlFor="marketRowLimit" className="small text-muted mb-1 d-block" style={{ fontSize: '0.72rem' }}>
                            Fetch Size
                          </label>
                          <select
                            id="marketRowLimit"
                            className="form-select form-select-sm"
                            value={marketRowLimit}
                            onChange={(e) => {
                              const nextLimit = Number(e.target.value);
                              setMarketRowLimit(nextLimit);
                            }}
                            style={{
                              fontSize: '0.78rem',
                              padding: '0.35rem 1.05rem 0.35rem 0.35rem',
                              minHeight: '38px',
                              width: '70px',
                              borderWidth: '1px',
                              borderColor: '#94a3b8',
                              backgroundColor: darkTheme ? '#1e293b' : '#fff',
                              color: darkTheme ? '#e2e8f0' : '#0f172a'
                            }}
                          >
                            {MARKET_ROW_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>
                        <button
                          type="button"
                          onClick={refreshVisibleMarketData}
                          disabled={
                            (!showMostActive && !showGainers && !showLosers && !showPreferredStocks) ||
                            activeLoading || gainersLoading || losersLoading
                          }
                          className="btn btn-sm"
                          style={{
                            background: '#2563eb',
                            border: '1px solid #1d4ed8',
                            color: '#fff',
                            padding: '0.4rem 0.8rem',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            minHeight: '38px',
                            opacity: ((!showMostActive && !showGainers && !showLosers && !showPreferredStocks) || activeLoading || gainersLoading || losersLoading) ? 0.6 : 1,
                            cursor: ((!showMostActive && !showGainers && !showLosers && !showPreferredStocks) || activeLoading || gainersLoading || losersLoading) ? 'not-allowed' : 'pointer'
                          }}
                          title="Refresh currently open list"
                          aria-label="Refresh"
                        >
                          🔄
                        </button>
                        {/* Stock Symbol Input */}
                        <div className="ms-2" style={{minWidth:'80px'}}>
                          <label htmlFor="symbolInput" className="small text-muted mb-1 d-block" style={{fontSize: '0.75rem', fontWeight: 'normal'}}>
                            Stock Symbol
                          </label>
                          <input
                            id="symbolInput"
                            type="text"
                            value={inputSymbol}
                            onChange={(e) => setInputSymbol(e.target.value.toUpperCase())}
                            placeholder="E.g., AAPL"
                            className="form-control form-control-sm"
                            inputMode="latin"
                            pattern="[A-Za-z.:-]{1,10}"
                            title="Use 1–10 characters: letters, dot, colon, or hyphen"
                            autoComplete="off"
                            spellCheck={false}
                            style={{
                              width: '80px',
                              padding: '0.4rem 0.8rem',
                              borderWidth: '2px',
                              borderColor: '#0d6efd',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              backgroundColor: darkTheme ? '#1e293b' : '#fff',
                              color: darkTheme ? '#e2e8f0' : '#000',
                              transition: 'background-color 0.3s ease, color 0.3s ease'
                            }}
                          />
                        </div>
                      </div>

                      {/* Stock List Panel */}
                      {(showMostActive || showGainers || showLosers || showPreferredStocks) && (
                        <div 
                          className="rounded p-2 bg-white shadow-sm position-absolute" 
                          style={{
                            maxHeight: '300px',
                            maxWidth: '500px',
                            overflowY: 'auto',
                            backgroundColor: showMostActive ? '#f8f9fa' : showGainers ? '#ccfbf1' : showLosers ? '#fecdd3' : '#ede9fe',
                            border: `2px solid ${showMostActive ? '#f59e42' : showGainers ? '#0f766e' : showLosers ? '#be123c' : '#6d28d9'}`,
                            zIndex: 1000,
                            top: '100%',
                            left: 0,
                            marginTop: '0.5rem'
                          }}
                        >
                          <div className="row g-1">
                            {(showMostActive ? activeLoading : showGainers ? gainersLoading : showLosers ? losersLoading : false) && (
                              <div className="col-12 px-2 py-3 text-center">
                                <div className="small text-muted">Fetching market data from Yahoo Finance...</div>
                                <div className="spinner-border spinner-border-sm mt-2" role="status" style={{color: showMostActive ? '#f59e42' : showGainers ? '#0d9488' : '#e11d48'}}>
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                              </div>
                            )}
                            {!(showMostActive ? activeLoading : showGainers ? gainersLoading : showLosers ? losersLoading : false) && (showMostActive ? mostActive : showGainers ? topGainers : showLosers ? topLosers : preferredStocks).length === 0 && (
                              <div className="col-12 px-2 py-3 text-center text-muted small">
                                {showPreferredStocks
                                  ? 'No preferred stocks saved yet.'
                                  : (showMostActive ? hasRequestedMostActive : showGainers ? hasRequestedGainers : showLosers ? hasRequestedLosers : false)
                                    ? 'No stocks available yet. Try again in a moment.'
                                    : 'Fetching market data from Yahoo Finance...'}
                              </div>
                            )}
                            {!(showMostActive ? activeLoading : showGainers ? gainersLoading : showLosers ? losersLoading : false) && (showMostActive ? mostActive : showGainers ? topGainers : showLosers ? topLosers : preferredStocks).map((stock) => {
                              const symbol = typeof stock === 'string' ? stock : stock.symbol;
                              return (
                              <div key={symbol} className="col-12 col-md-6">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setInputSymbol(symbol);
                                    setShowMostActive(false);
                                    setShowGainers(false);
                                    setShowLosers(false);
                                    setShowPreferredStocks(false);
                                    fetchReportsOnly(symbol);
                                  }}
                                  className="btn btn-sm w-100 text-start d-flex justify-content-between align-items-center"
                                  style={{
                                    backgroundColor: '#fff',
                                    border: '1px solid #dee2e6',
                                    padding: '0.4rem 0.6rem',
                                    fontSize: '0.75rem',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = showMostActive ? '#dbeafe' : showGainers ? '#ccfbf1' : showLosers ? '#fecdd3' : '#ede9fe';
                                    e.currentTarget.style.borderColor = showMostActive ? '#3b82f6' : showGainers ? '#0d9488' : showLosers ? '#e11d48' : '#7c3aed';
                                    e.currentTarget.style.transform = 'translateX(4px)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#fff';
                                    e.currentTarget.style.borderColor = '#dee2e6';
                                    e.currentTarget.style.transform = 'translateX(0)';
                                  }}
                                >
                                  <div className="d-flex flex-column flex-grow-1" style={{minWidth: 0}}>
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                      <span className="fw-semibold">{symbol}</span>
                                      {typeof stock !== 'string' && ((stock.change !== undefined && stock.change !== null) || (stock.regularMarketChangePercent !== undefined && stock.regularMarketChangePercent !== null)) && (
                                        <span className={`badge ${(stock.change || stock.regularMarketChangePercent) >= 0 ? 'bg-success' : 'bg-danger'}`} style={{fontSize: '0.7rem'}}>
                                          {(stock.change || stock.regularMarketChangePercent) >= 0 ? '+' : ''}{(stock.change || stock.regularMarketChangePercent).toFixed(2)}%
                                        </span>
                                      )}
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                      {typeof stock === 'string' ? (
                                        <span className="text-muted small" style={{fontSize: '0.65rem'}}>Preferred stock</span>
                                      ) : (
                                        <>
                                          <span className="text-muted small text-truncate" style={{fontSize: '0.65rem', flex: 1}}>
                                            {stock.name || stock.shortName || stock.longName}
                                          </span>
                                          {(stock.price || stock.lastPrice || stock.current_price || stock.regularMarketPrice) && (
                                            <span className="fw-bold text-primary ms-2" style={{fontSize: '0.75rem', whiteSpace: 'nowrap'}}>
                                              ${(stock.price || stock.lastPrice || stock.current_price || stock.regularMarketPrice)}
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </button>
                              </div>
                            );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                
                {/* Action Buttons Section */}
                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                  <div className="mb-2 pb-2" style={{borderBottom: darkTheme ? '1px solid #475569' : '1px solid #dee2e6'}}>
                    <h6 className="mb-0" style={{fontSize: '0.85rem', fontWeight: '600', color: darkTheme ? '#94a3b8' : '#6c757d'}}>
                      🚀 AI Agent Workspace
                    </h6>
                  </div>
                  
                  <div className="action-buttons-grid d-flex gap-2 flex-wrap w-100 justify-content-start">
                <button
                  type="button"
                  onClick={handleStockOverview}
                  className={`action-btn btn btn-gradient d-flex flex-column align-items-center justify-content-center position-relative ${activeButton === 'overview' ? 'shadow-lg' : ''}`}
                  disabled={isLoading || !inputSymbol.trim()}
                  style={{
                    background: activeButton === 'overview' 
                      ? 'linear-gradient(90deg, #38bdf8 0%, #0ea5e9 100%)' 
                      : 'linear-gradient(90deg, #bae6fd 0%, #7dd3fc 100%)',
                    border: activeButton === 'overview' ? '2px solid #0ea5e9' : 'none',
                    color:'#fff',
                    transform: activeButton === 'overview' ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.2s ease',
                    fontSize: '0.8rem',
                    padding: '0.5rem',
                    minHeight: '55px',
                    flex: '1 1 auto',
                    minWidth: '48px',
                    maxWidth: '70px',
                    ...actionButtonSizing,
                    opacity: (isLoading || !inputSymbol.trim()) ? 0.6 : 1
                  }}
                >
                  <span className="mb-1" aria-hidden style={{fontSize: '1.3rem'}}>📋</span>
                  <span style={{fontSize: '0.7rem', textAlign: 'center', lineHeight: '1'}}>Overview</span>
                </button>
                <button
                  type="button"
                  onClick={handleGetCharts}
                  className={`action-btn btn btn-gradient d-flex flex-column align-items-center justify-content-center position-relative ${activeButton === 'reports' ? 'shadow-lg' : ''}`}
                  disabled={isLoading || !inputSymbol.trim()}
                  style={{
                    background: activeButton === 'reports' 
                      ? 'linear-gradient(90deg,#1e40af,#1e3a8a)' 
                      : 'linear-gradient(90deg,#2563eb,#1e40af)',
                    border: activeButton === 'reports' ? '2px solid #1e40af' : 'none',
                    color:'#fff',
                    transform: activeButton === 'reports' ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.2s ease',
                    fontSize: '0.8rem',
                    padding: '0.5rem',
                    minHeight: '55px',
                    flex: '1 1 auto',
                    minWidth: '48px',
                    maxWidth: '70px',
                    ...actionButtonSizing,
                    opacity: (isLoading || !inputSymbol.trim()) ? 0.6 : 1
                  }}
                >
                  {isLoading && activeButton === 'reports' && (
                    <span className="spinner-border spinner-border-sm mb-1" role="status" aria-hidden="true" style={{width: '0.8rem', height: '0.8rem'}}></span>
                  )}
                  <span className="mb-1" aria-hidden style={{fontSize: '1.3rem'}}>📊</span>
                  <span style={{fontSize: '0.7rem', textAlign: 'center', lineHeight: '1'}}>Charts</span>
                </button>
                <button
                  type="button"
                  onClick={handleGetNews}
                  className={`action-btn btn btn-gradient d-flex flex-column align-items-center justify-content-center position-relative ${activeButton === 'news' ? 'shadow-lg' : ''}`}
                  disabled={isGuestUser || newsLoading || !inputSymbol.trim()}
                  style={{
                    background: activeButton === 'news' 
                      ? 'linear-gradient(90deg,#fb923c,#f97316)' 
                      : 'linear-gradient(90deg,#fb923c,#fb923c)',
                    border: activeButton === 'news' ? '2px solid #f97316' : 'none',
                    color:'#fff',
                    transform: activeButton === 'news' ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.2s ease',
                    fontSize: '0.8rem',
                    padding: '0.5rem',
                    minHeight: '55px',
                    opacity: (isGuestUser || newsLoading || !inputSymbol.trim()) ? 0.6 : 1,
                    flex: '1 1 auto',
                    minWidth: '48px',
                    maxWidth: '70px',
                    ...actionButtonSizing
                  }}
                >
                  {newsLoading && activeButton === 'news' && (
                    <span className="spinner-border spinner-border-sm mb-1" role="status" aria-hidden="true" style={{width: '0.8rem', height: '0.8rem'}}></span>
                  )}
                  <span className="mb-1" aria-hidden style={{fontSize: '1.3rem'}}>📰</span>
                  <span style={{fontSize: '0.7rem', textAlign: 'center', lineHeight: '1'}}>News</span>
                </button>
                <button
                  type="button"
                  onClick={handleAnalyzeWithAI}
                  className={`action-btn btn btn-gradient d-flex flex-column align-items-center justify-content-center position-relative ${activeButton === 'ai' ? 'shadow-lg' : ''}`}
                  disabled={isGuestUser || isLoading || !inputSymbol.trim()}
                  style={{
                    background: activeButton === 'ai' 
                      ? 'linear-gradient(90deg,#9333ea,#7e22ce)' 
                      : 'linear-gradient(90deg,#c084fc,#a855f7)',
                    border: activeButton === 'ai' ? '2px solid #7e22ce' : 'none',
                    color:'#fff',
                    transform: activeButton === 'ai' ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.2s ease',
                    fontSize: '0.8rem',
                    padding: '0.5rem',
                    minHeight: '55px',
                    flex: '1 1 auto',
                    minWidth: '48px',
                    maxWidth: '70px',
                    ...actionButtonSizing
                  }}
                >
                  {isLoading && activeButton === 'ai' && (
                    <span className="spinner-border spinner-border-sm mb-1" role="status" aria-hidden="true" style={{width: '0.8rem', height: '0.8rem'}}></span>
                  )}
                  <span className="mb-1" aria-hidden style={{fontSize: '1.3rem'}}>🤖</span>
                  <span style={{fontSize: '0.7rem', textAlign: 'center', lineHeight: '1'}}>Swing Trade Advisor</span>
                </button>
                <button
                  type="button"
                  onClick={handleAnalyzeDayTrading}
                  className={`action-btn btn btn-gradient d-flex flex-column align-items-center justify-content-center position-relative ${activeButton === 'day-ai' ? 'shadow-lg' : ''}`}
                  disabled={isGuestUser || isLoading || !inputSymbol.trim()}
                  style={{
                    background: activeButton === 'day-ai'
                      ? 'linear-gradient(90deg,#ea580c,#c2410c)'
                      : 'linear-gradient(90deg,#fb923c,#f97316)',
                    border: activeButton === 'day-ai' ? '2px solid #c2410c' : 'none',
                    color:'#fff',
                    transform: activeButton === 'day-ai' ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.2s ease',
                    fontSize: '0.8rem',
                    padding: '0.5rem',
                    minHeight: '55px',
                    flex: '1 1 auto',
                    minWidth: '48px',
                    maxWidth: '90px',
                    ...actionButtonSizing
                  }}
                >
                  {isLoading && activeButton === 'day-ai' && (
                    <span className="spinner-border spinner-border-sm mb-1" role="status" aria-hidden="true" style={{width: '0.8rem', height: '0.8rem'}}></span>
                  )}
                  <span className="mb-1" aria-hidden style={{fontSize: '1.3rem'}}>🤖</span>
                  <span style={{fontSize: '0.7rem', textAlign: 'center', lineHeight: '1'}}>Day Trade Advisor</span>
                </button>
                <button
                  type="button"
                  onClick={handleAnalyzeCentiment}
                  className={`action-btn btn btn-gradient d-flex flex-column align-items-center justify-content-center position-relative ${activeButton === 'centiment-ai' ? 'shadow-lg' : ''}`}
                  disabled={isGuestUser || isLoading || !inputSymbol.trim()}
                  style={{
                    background: activeButton === 'centiment-ai'
                      ? 'linear-gradient(90deg,#0f766e,#115e59)'
                      : 'linear-gradient(90deg,#14b8a6,#0d9488)',
                    border: activeButton === 'centiment-ai' ? '2px solid #115e59' : 'none',
                    color:'#fff',
                    transform: activeButton === 'centiment-ai' ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.2s ease',
                    fontSize: '0.8rem',
                    padding: '0.5rem',
                    minHeight: '55px',
                    flex: '1 1 auto',
                    minWidth: '48px',
                    maxWidth: '90px',
                    ...actionButtonSizing
                  }}
                >
                  {isLoading && activeButton === 'centiment-ai' && (
                    <span className="spinner-border spinner-border-sm mb-1" role="status" aria-hidden="true" style={{width: '0.8rem', height: '0.8rem'}}></span>
                  )}
                  <span className="mb-1" aria-hidden style={{fontSize: '1.3rem'}}>🤖</span>
                  <span style={{fontSize: '0.7rem', textAlign: 'center', lineHeight: '1'}}>Centiment Advisor</span>
                </button>
                <button
                  type="button"
                  onClick={handlePhaseTwoAdvisors}
                  className={`action-btn btn btn-gradient d-flex flex-column align-items-center justify-content-center position-relative ${activeButton === 'phase2-advisors' ? 'shadow-lg' : ''}`}
                  disabled={isGuestUser || isLoading || phaseTwoAdvisorsLoading || !inputSymbol.trim()}
                  style={{
                    background: activeButton === 'phase2-advisors'
                      ? 'linear-gradient(90deg,#7c3aed,#6d28d9)'
                      : 'linear-gradient(90deg,#a78bfa,#8b5cf6)',
                    border: activeButton === 'phase2-advisors' ? '2px solid #6d28d9' : 'none',
                    color:'#fff',
                    transform: activeButton === 'phase2-advisors' ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.2s ease',
                    fontSize: '0.8rem',
                    padding: '0.5rem',
                    minHeight: '55px',
                    flex: '1 1 auto',
                    minWidth: '48px',
                    maxWidth: '95px',
                    ...actionButtonSizing
                  }}
                >
                  {phaseTwoAdvisorsLoading && activeButton === 'phase2-advisors' && (
                    <span className="spinner-border spinner-border-sm mb-1" role="status" aria-hidden="true" style={{width: '0.8rem', height: '0.8rem'}}></span>
                  )}
                  <span className="mb-1" aria-hidden style={{fontSize: '1.3rem'}}>🧭</span>
                  <span style={{fontSize: '0.7rem', textAlign: 'center', lineHeight: '1'}}>Regime, Momentum & Rotation</span>
                </button>
                <button
                  type="button"
                  onClick={handlePhaseThreeAdvisors}
                  className={`action-btn btn btn-gradient d-flex flex-column align-items-center justify-content-center position-relative ${activeButton === 'phase3-advisors' ? 'shadow-lg' : ''}`}
                  disabled={isGuestUser || isLoading || phaseThreeAdvisorsLoading || !inputSymbol.trim()}
                  style={{
                    background: activeButton === 'phase3-advisors'
                      ? 'linear-gradient(90deg,#be123c,#9f1239)'
                      : 'linear-gradient(90deg,#fb7185,#f43f5e)',
                    border: activeButton === 'phase3-advisors' ? '2px solid #9f1239' : 'none',
                    color:'#fff',
                    transform: activeButton === 'phase3-advisors' ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.2s ease',
                    fontSize: '0.8rem',
                    padding: '0.5rem',
                    minHeight: '55px',
                    flex: '1 1 auto',
                    minWidth: '48px',
                    maxWidth: '95px',
                    ...actionButtonSizing
                  }}
                >
                  {phaseThreeAdvisorsLoading && activeButton === 'phase3-advisors' && (
                    <span className="spinner-border spinner-border-sm mb-1" role="status" aria-hidden="true" style={{width: '0.8rem', height: '0.8rem'}}></span>
                  )}
                  <span className="mb-1" aria-hidden style={{fontSize: '1.3rem'}}>🧩</span>
                  <span style={{fontSize: '0.7rem', textAlign: 'center', lineHeight: '1'}}>Risk Intelligence & Options Strategy</span>
                </button>
                <button
                  type="button"
                  onClick={handlePhaseOneAdvisors}
                  className={`action-btn btn btn-gradient d-flex flex-column align-items-center justify-content-center position-relative ${activeButton === 'phase1-advisors' ? 'shadow-lg' : ''}`}
                  disabled={isGuestUser || isLoading || phaseOneAdvisorsLoading || !inputSymbol.trim()}
                  style={{
                    background: activeButton === 'phase1-advisors'
                      ? 'linear-gradient(90deg,#0891b2,#0e7490)'
                      : 'linear-gradient(90deg,#22d3ee,#06b6d4)',
                    border: activeButton === 'phase1-advisors' ? '2px solid #0e7490' : 'none',
                    color:'#fff',
                    transform: activeButton === 'phase1-advisors' ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.2s ease',
                    fontSize: '0.8rem',
                    padding: '0.5rem',
                    minHeight: '55px',
                    flex: '1 1 auto',
                    minWidth: '48px',
                    maxWidth: '95px',
                    ...actionButtonSizing
                  }}
                >
                  {phaseOneAdvisorsLoading && activeButton === 'phase1-advisors' && (
                    <span className="spinner-border spinner-border-sm mb-1" role="status" aria-hidden="true" style={{width: '0.8rem', height: '0.8rem'}}></span>
                  )}
                  <span className="mb-1" aria-hidden style={{fontSize: '1.3rem'}}>📐</span>
                  <span style={{fontSize: '0.7rem', textAlign: 'center', lineHeight: '1'}}>Trade Setup Tools</span>
                </button>
                <button
                  type="button"
                  onClick={handleShowReports}
                  className={`action-btn btn btn-gradient d-flex flex-column align-items-center justify-content-center position-relative ${activeButton === 'printable' ? 'shadow-lg' : ''}`}
                  disabled={isGuestUser || !selectedStock}
                  style={{
                    background: activeButton === 'printable' 
                      ? 'linear-gradient(90deg,#6b7280,#4b5563)' 
                      : 'linear-gradient(90deg,#9ca3af,#6b7280)',
                    border: activeButton === 'printable' ? '2px solid #4b5563' : 'none',
                    color:'#fff',
                    transform: activeButton === 'printable' ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.2s ease',
                    fontSize: '0.8rem',
                    padding: '0.5rem',
                    minHeight: '55px',
                    flex: '1 1 auto',
                    minWidth: '48px',
                    maxWidth: '70px',
                    ...actionButtonSizing
                  }}
                >
                  <span className="mb-1" aria-hidden style={{fontSize: '1.3rem'}}>🖨️</span>
                  <span style={{fontSize: '0.7rem', textAlign: 'center', lineHeight: '1'}}>Reports</span>
                </button>
                <button
                  type="button"
                  onClick={handleOpenAccountScreen}
                  className={`action-btn btn btn-gradient d-flex flex-column align-items-center justify-content-center position-relative ${activeButton === 'account' ? 'shadow-lg' : ''}`}
                  disabled={isGuestUser}
                  style={{
                    background: activeButton === 'account'
                      ? 'linear-gradient(90deg,#0f172a,#1e293b)'
                      : 'linear-gradient(90deg,#475569,#334155)',
                    border: activeButton === 'account' ? '2px solid #1e293b' : 'none',
                    color:'#fff',
                    transform: activeButton === 'account' ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.2s ease',
                    fontSize: '0.8rem',
                    padding: '0.5rem',
                    minHeight: '55px',
                    flex: '1 1 auto',
                    minWidth: '48px',
                    maxWidth: '90px',
                    ...actionButtonSizing
                  }}
                >
                  <span className="mb-1" aria-hidden style={{fontSize: '1.3rem'}}>👤</span>
                  <span style={{fontSize: '0.7rem', textAlign: 'center', lineHeight: '1'}}>Account</span>
                </button>
                <button
                  type="button"
                  onClick={handleOpenAdminAccess}
                  className={`action-btn btn btn-gradient d-flex flex-column align-items-center justify-content-center position-relative ${activeButton === 'user-management' ? 'shadow-lg' : ''}`}
                  disabled={isGuestUser}
                  style={{
                    background: activeButton === 'user-management'
                      ? 'linear-gradient(90deg,#1d4ed8,#1e3a8a)'
                      : 'linear-gradient(90deg,#3b82f6,#2563eb)',
                    border: activeButton === 'user-management' ? '2px solid #1e3a8a' : 'none',
                    color:'#fff',
                    transform: activeButton === 'user-management' ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.2s ease',
                    fontSize: '0.8rem',
                    padding: '0.5rem',
                    minHeight: '55px',
                    flex: '1 1 auto',
                    minWidth: '48px',
                    maxWidth: '105px',
                    ...actionButtonSizing
                  }}
                >
                  <span className="mb-1" aria-hidden style={{fontSize: '1.3rem'}}>🛠️</span>
                  <span style={{fontSize: '0.7rem', textAlign: 'center', lineHeight: '1'}}>Admin</span>
                </button>
                  </div>
                </div>
              </div>
              )}

              <div className="mt-2">
                {selectedStock ? (
                  <div className="alert alert-success mb-0 py-2" style={{fontSize: '0.85rem'}}>
                    <strong>✓ Currently Viewing:</strong> <span style={{fontWeight: '600'}}>{selectedStock}</span>
                  </div>
                ) : (
                  <div className="alert alert-info mb-0 py-2" style={{fontSize: '0.85rem'}}>
                    <strong>ℹ️ Quick Start:</strong> Select stock symbol by using the Active, Gainers, or Losers list. You can type the symbol as well. After having the value in Stock Symbol text box, click an action button to fetch data.
                  </div>
                )}
              </div>
            </div>
          </form>

          {isLoading && (
            <div className="alert alert-info text-center" role="alert">
              Loading stock data and analysis...
            </div>
          )}

          {error && (
            <div className="alert alert-danger" role="alert">{error}</div>
          )}

          {stockData && !error && null}

          {activeTab === 'account' && (
            <UserManagement
              apiBaseUrl={API_BASE_URL}
              currentUser={currentUser}
              screen="account"
              requestedAuthMode={userManagementAuthMode}
              onAuthChange={(userWithToken) => persistAuth(userWithToken, userWithToken.token)}
              onLogout={clearAuth}
              onPreferredStocksChange={updateCurrentUserPreferredStocks}
              onSelectStock={(symbol) => setInputSymbol(symbol)}
              darkTheme={darkTheme}
            />
          )}

          {activeTab === 'user-management' && (
            <UserManagement
              apiBaseUrl={API_BASE_URL}
              currentUser={currentUser}
              screen="admin"
              requestedAuthMode={userManagementAuthMode}
              onAuthChange={(userWithToken) => persistAuth(userWithToken, userWithToken.token)}
              onLogout={clearAuth}
              onPreferredStocksChange={updateCurrentUserPreferredStocks}
              onSelectStock={(symbol) => setInputSymbol(symbol)}
              darkTheme={darkTheme}
            />
          )}
        </div>

      </div>

      {/* Tab Navigation */}
      {stockData && !error && (
        <div className="mt-4 w-100 d-print-none px-2 px-md-3">
          <ul className="nav nav-tabs nav-fill" role="tablist" style={{
            borderBottom: darkTheme ? '1px solid #475569' : '1px solid #dee2e6'
          }}>
            <li className="nav-item" role="presentation">
              <button 
                className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('overview');
                  // Clear error when switching to Overview so data can be displayed
                  if (stockData || historicalData) {
                    setError(null);
                  }
                }}
                type="button"
                role="tab"
                style={{
                  backgroundColor: activeTab === 'overview' ? (darkTheme ? '#334155' : '#fff') : (darkTheme ? '#1e293b' : '#fff'),
                  color: activeTab === 'overview' ? (darkTheme ? '#60a5fa' : '#0d6efd') : (darkTheme ? '#cbd5e1' : '#6c757d'),
                  borderColor: darkTheme ? '#475569' : '#dee2e6',
                  transition: 'all 0.3s ease'
                }}
              >
                📊 Stock Overview
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button 
                className={`nav-link ${activeTab === 'charts' ? 'active' : ''}`}
                onClick={() => setActiveTab('charts')}
                type="button"
                role="tab"
                style={{
                  backgroundColor: activeTab === 'charts' ? (darkTheme ? '#334155' : '#fff') : (darkTheme ? '#1e293b' : '#fff'),
                  color: activeTab === 'charts' ? (darkTheme ? '#60a5fa' : '#0d6efd') : (darkTheme ? '#cbd5e1' : '#6c757d'),
                  borderColor: darkTheme ? '#475569' : '#dee2e6',
                  transition: 'all 0.3s ease'
                }}
              >
                📈 Charts
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button 
                className={`nav-link ${activeTab === 'company-news' ? 'active' : ''} ${!companyNews ? 'disabled' : ''}`}
                onClick={() => companyNews && setActiveTab('company-news')}
                type="button"
                role="tab"
                disabled={!companyNews}
                style={{
                  backgroundColor: activeTab === 'company-news' ? (darkTheme ? '#334155' : '#fff') : (darkTheme ? '#1e293b' : '#fff'),
                  color: !companyNews ? (darkTheme ? '#64748b' : '#adb5bd') : (activeTab === 'company-news' ? (darkTheme ? '#60a5fa' : '#0d6efd') : (darkTheme ? '#cbd5e1' : '#6c757d')),
                  borderColor: darkTheme ? '#475569' : '#dee2e6',
                  cursor: !companyNews ? 'not-allowed' : 'pointer',
                  opacity: !companyNews ? 0.5 : 1,
                  transition: 'all 0.3s ease'
                }}
                title={!companyNews ? 'Click "News" button to enable this tab' : ''}
              >
                📰 Company News
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button 
                className={`nav-link ${activeTab === 'ai-analysis' ? 'active' : ''} ${!aiAnalysis ? 'disabled' : ''}`}
                onClick={() => aiAnalysis && setActiveTab('ai-analysis')}
                type="button"
                role="tab"
                disabled={!aiAnalysis}
                style={{
                  backgroundColor: activeTab === 'ai-analysis' ? (darkTheme ? '#334155' : '#fff') : (darkTheme ? '#1e293b' : '#fff'),
                  color: !aiAnalysis ? (darkTheme ? '#64748b' : '#adb5bd') : (activeTab === 'ai-analysis' ? (darkTheme ? '#60a5fa' : '#0d6efd') : (darkTheme ? '#cbd5e1' : '#6c757d')),
                  borderColor: darkTheme ? '#475569' : '#dee2e6',
                  cursor: !aiAnalysis ? 'not-allowed' : 'pointer',
                  opacity: !aiAnalysis ? 0.5 : 1,
                  transition: 'all 0.3s ease'
                }}
                title={!aiAnalysis ? 'Click "Analyze using AI" to enable this tab' : ''}
              >
                🤖 AI Swing Advisor
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'day-ai-analysis' ? 'active' : ''} ${!dayAiAnalysis ? 'disabled' : ''}`}
                onClick={() => dayAiAnalysis && setActiveTab('day-ai-analysis')}
                type="button"
                role="tab"
                disabled={!dayAiAnalysis}
                style={{
                  backgroundColor: activeTab === 'day-ai-analysis' ? (darkTheme ? '#334155' : '#fff') : (darkTheme ? '#1e293b' : '#fff'),
                  color: !dayAiAnalysis ? (darkTheme ? '#64748b' : '#adb5bd') : (activeTab === 'day-ai-analysis' ? (darkTheme ? '#fb923c' : '#ea580c') : (darkTheme ? '#cbd5e1' : '#6c757d')),
                  borderColor: darkTheme ? '#475569' : '#dee2e6',
                  cursor: !dayAiAnalysis ? 'not-allowed' : 'pointer',
                  opacity: !dayAiAnalysis ? 0.5 : 1,
                  transition: 'all 0.3s ease'
                }}
                title={!dayAiAnalysis ? 'Click "Day Trade Advisor" button to enable this tab' : ''}
              >
                ⚡ Day Trade Advisor
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'centiment-ai-analysis' ? 'active' : ''} ${!centimentAnalysis ? 'disabled' : ''}`}
                onClick={() => centimentAnalysis && setActiveTab('centiment-ai-analysis')}
                type="button"
                role="tab"
                disabled={!centimentAnalysis}
                style={{
                  backgroundColor: activeTab === 'centiment-ai-analysis' ? (darkTheme ? '#334155' : '#fff') : (darkTheme ? '#1e293b' : '#fff'),
                  color: !centimentAnalysis ? (darkTheme ? '#64748b' : '#adb5bd') : (activeTab === 'centiment-ai-analysis' ? (darkTheme ? '#2dd4bf' : '#0f766e') : (darkTheme ? '#cbd5e1' : '#6c757d')),
                  borderColor: darkTheme ? '#475569' : '#dee2e6',
                  cursor: !centimentAnalysis ? 'not-allowed' : 'pointer',
                  opacity: !centimentAnalysis ? 0.5 : 1,
                  transition: 'all 0.3s ease'
                }}
                title={!centimentAnalysis ? 'Click "Centiment Advisor" button to enable this tab' : ''}
              >
                🤖 Centiment Advisor
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'phase1-advisors' ? 'active' : ''} ${!phaseOneAdvisors ? 'disabled' : ''}`}
                onClick={() => phaseOneAdvisors && setActiveTab('phase1-advisors')}
                type="button"
                role="tab"
                disabled={!phaseOneAdvisors}
                style={{
                  backgroundColor: activeTab === 'phase1-advisors' ? (darkTheme ? '#334155' : '#fff') : (darkTheme ? '#1e293b' : '#fff'),
                  color: !phaseOneAdvisors ? (darkTheme ? '#64748b' : '#adb5bd') : (activeTab === 'phase1-advisors' ? (darkTheme ? '#22d3ee' : '#0e7490') : (darkTheme ? '#cbd5e1' : '#6c757d')),
                  borderColor: darkTheme ? '#475569' : '#dee2e6',
                  cursor: !phaseOneAdvisors ? 'not-allowed' : 'pointer',
                  opacity: !phaseOneAdvisors ? 0.5 : 1,
                  transition: 'all 0.3s ease'
                }}
                title={!phaseOneAdvisors ? 'Click "Trade Setup Tools" button to enable this tab' : ''}
              >
                📐 Trade Setup Tools
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'phase2-advisors' ? 'active' : ''} ${!phaseTwoAdvisors ? 'disabled' : ''}`}
                onClick={() => phaseTwoAdvisors && setActiveTab('phase2-advisors')}
                type="button"
                role="tab"
                disabled={!phaseTwoAdvisors}
                style={{
                  backgroundColor: activeTab === 'phase2-advisors' ? (darkTheme ? '#334155' : '#fff') : (darkTheme ? '#1e293b' : '#fff'),
                  color: !phaseTwoAdvisors ? (darkTheme ? '#64748b' : '#adb5bd') : (activeTab === 'phase2-advisors' ? (darkTheme ? '#a78bfa' : '#6d28d9') : (darkTheme ? '#cbd5e1' : '#6c757d')),
                  borderColor: darkTheme ? '#475569' : '#dee2e6',
                  cursor: !phaseTwoAdvisors ? 'not-allowed' : 'pointer',
                  opacity: !phaseTwoAdvisors ? 0.5 : 1,
                  transition: 'all 0.3s ease'
                }}
                title={!phaseTwoAdvisors ? 'Click "Regime, Momentum & Rotation" button to enable this tab' : ''}
              >
                🧭 Regime, Momentum & Rotation
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'phase3-advisors' ? 'active' : ''} ${!phaseThreeAdvisors ? 'disabled' : ''}`}
                onClick={() => phaseThreeAdvisors && setActiveTab('phase3-advisors')}
                type="button"
                role="tab"
                disabled={!phaseThreeAdvisors}
                style={{
                  backgroundColor: activeTab === 'phase3-advisors' ? (darkTheme ? '#334155' : '#fff') : (darkTheme ? '#1e293b' : '#fff'),
                  color: !phaseThreeAdvisors ? (darkTheme ? '#64748b' : '#adb5bd') : (activeTab === 'phase3-advisors' ? (darkTheme ? '#fb7185' : '#be123c') : (darkTheme ? '#cbd5e1' : '#6c757d')),
                  borderColor: darkTheme ? '#475569' : '#dee2e6',
                  cursor: !phaseThreeAdvisors ? 'not-allowed' : 'pointer',
                  opacity: !phaseThreeAdvisors ? 0.5 : 1,
                  transition: 'all 0.3s ease'
                }}
                title={!phaseThreeAdvisors ? 'Click "Risk Intelligence & Options Strategy" button to enable this tab' : ''}
              >
                🧩 Risk Intelligence & Options Strategy
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button 
                className={`nav-link ${activeTab === 'printable' ? 'active' : ''}`}
                onClick={() => setActiveTab('printable')}
                type="button"
                role="tab"
                style={{
                  backgroundColor: activeTab === 'printable' ? (darkTheme ? '#334155' : '#fff') : (darkTheme ? '#1e293b' : '#fff'),
                  color: activeTab === 'printable' ? (darkTheme ? '#60a5fa' : '#0d6efd') : (darkTheme ? '#cbd5e1' : '#6c757d'),
                  borderColor: darkTheme ? '#475569' : '#dee2e6',
                  transition: 'all 0.3s ease'
                }}
              >
                🖨️ Print Report
              </button>
            </li>
          </ul>
        </div>
      )}

      {/* Full-width Stock Data table */}
      {stockData && activeTab === 'overview' && (
        <div className="mt-3 mt-md-4" style={{width:'100%', maxWidth:'100%', overflowX:'hidden'}}>
          <div className="card shadow-sm mx-2 mx-md-3" style={{
            backgroundColor: darkTheme ? '#334155' : '#fff',
            borderColor: darkTheme ? '#475569' : '#dee2e6',
            transition: 'background-color 0.3s ease'
          }}>
            <div className="card-header d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2" style={{
              background: darkTheme ? 'linear-gradient(90deg, #1e3a5f 0%, #2c5282 100%)' : 'linear-gradient(90deg, #0ea5e9 0%, #0284c7 100%)',
              color: '#fff'
            }}>
              <h2 className="h6 h5-md mb-0">Stock Data</h2>
              {stockData.last_updated && (
                <span className="badge" style={{fontSize: '0.7rem', backgroundColor: darkTheme ? '#0f172a' : '#fff', color: darkTheme ? '#60a5fa' : '#0ea5e9'}}>
                  Last Updated: {stockData.last_updated}
                </span>
              )}
            </div>
            
            {/* Unified Card Layout for both Mobile and Desktop */}
            <div className="card-body p-2 p-md-3" style={{backgroundColor: darkTheme ? '#334155' : '#fff'}}>
              <div className="row g-2 g-md-3">
                <div className="col-12 col-md-4">
                  <div className="p-2 p-md-3 rounded border" style={{backgroundColor: darkTheme ? '#1e3a5f' : '#e8f5e9', borderColor: darkTheme ? '#2d5b7f' : '#a5d6a7'}}>
                    <small className="d-block" style={{fontSize: '0.7rem', color: darkTheme ? '#86efac' : '#2e7d32'}}>Company</small>
                    <div className="fw-semibold" style={{fontSize: '0.8rem', color: darkTheme ? '#bbf7d0' : '#1b5e20'}}>({stockData.symbol}) {stockData.name}</div>
                  </div>
                </div>
                <div className="col-12 col-md-8">
                  <div className="p-2 p-md-3 rounded border" style={{backgroundColor: darkTheme ? '#1f2a44' : '#eef2ff', borderColor: darkTheme ? '#334155' : '#c7d2fe'}}>
                    <small className="d-block" style={{fontSize: '0.7rem', color: darkTheme ? '#a5b4fc' : '#3730a3'}}>
                      Industry: {stockData.industry || 'N/A'}
                    </small>
                    <div style={{fontSize: '0.78rem', color: darkTheme ? '#e2e8f0' : '#1f2937'}}>
                      {stockData.short_description && stockData.short_description !== 'N/A'
                        ? stockData.short_description
                        : 'Company description is currently unavailable.'}
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-4">
                  <div className="p-2 p-md-3 rounded border" style={{backgroundColor: darkTheme ? '#1e3a5f' : '#e1f5fe', borderColor: darkTheme ? '#2d5b7f' : '#81d4fa'}}>
                    <small className="d-block" style={{fontSize: '0.7rem', color: darkTheme ? '#7dd3fc' : '#0277bd'}}>Current Price</small>
                    <div className="fw-bold" style={{fontSize: '0.9rem', color: darkTheme ? '#bfdbfe' : '#01579b'}}>${stockData.price}</div>
                  </div>
                </div>
                <div className="col-6 col-md-4">
                  <div className="p-2 p-md-3 rounded border" style={{backgroundColor: darkTheme ? '#3f1f3f' : '#fce4ec', borderColor: darkTheme ? '#5f2f5f' : '#f48fb1'}}>
                    <small className="d-block" style={{fontSize: '0.7rem', color: darkTheme ? '#fda4af' : '#c2185b'}}>Change</small>
                    <div className={`fw-bold ${stockData.change >= 0 ? 'text-success' : 'text-danger'}`} style={{fontSize: '0.8rem'}}>
                      {stockData.change > 0 ? '+' : ''}{parseFloat(stockData.change).toFixed(2)} ({parseFloat(stockData.percent_change).toFixed(2)}%)
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-4">
                  <div className="p-2 p-md-3 rounded border" style={{backgroundColor: darkTheme ? '#3f2f5f' : '#f3e5f5', borderColor: darkTheme ? '#5f3f7f' : '#ce93d8'}}>
                    <small className="d-block" style={{fontSize: '0.7rem', color: darkTheme ? '#e9d5ff' : '#7b1fa2'}}>Market Cap</small>
                    <div className="fw-semibold" style={{fontSize: '0.8rem', color: darkTheme ? '#f3e8ff' : '#4a148c'}}>{formatLargeNumber(stockData.market_cap)}</div>
                  </div>
                </div>
                <div className="col-6 col-md-4">
                  <div className="p-2 p-md-3 rounded border" style={{backgroundColor: darkTheme ? '#3f2f5f' : '#ede7f6', borderColor: darkTheme ? '#5f3f7f' : '#b39ddb'}}>
                    <small className="d-block" style={{fontSize: '0.7rem', color: darkTheme ? '#ddd6fe' : '#512da8'}}>Volume</small>
                    <div className="fw-semibold" style={{fontSize: '0.8rem', color: darkTheme ? '#ede9fe' : '#311b92'}}>{formatLargeNumber(stockData.volume)}</div>
                  </div>
                </div>
                <div className="col-6 col-md-4">
                  <div className="p-2 p-md-3 rounded border" style={{backgroundColor: darkTheme ? '#1f3f3f' : '#e0f2f1', borderColor: darkTheme ? '#2f5f5f' : '#80cbc4'}}>
                    <small className="d-block" style={{fontSize: '0.7rem', color: darkTheme ? '#5eead4' : '#00897b'}}>Day Low</small>
                    <div className="fw-semibold" style={{fontSize: '0.8rem', color: darkTheme ? '#99f6e4' : '#004d40'}}>${parseFloat(stockData.day_low).toFixed(2)}</div>
                  </div>
                </div>
                <div className="col-6 col-md-4">
                  <div className="p-2 p-md-3 rounded border" style={{backgroundColor: darkTheme ? '#5f4f1f' : '#fff8e1', borderColor: darkTheme ? '#7f6f2f' : '#ffe082'}}>
                    <small className="d-block" style={{fontSize: '0.7rem', color: darkTheme ? '#fde68a' : '#f57f17'}}>Day High</small>
                    <div className="fw-semibold" style={{fontSize: '0.8rem', color: darkTheme ? '#fef3c7' : '#f57f17'}}>${parseFloat(stockData.day_high).toFixed(2)}</div>
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="p-2 p-md-3 rounded border" style={{backgroundColor: darkTheme ? '#1f2f5f' : '#e8eaf6', borderColor: darkTheme ? '#2f3f7f' : '#9fa8da'}}>
                    <small className="d-block" style={{fontSize: '0.7rem', color: darkTheme ? '#a5b4fc' : '#3949ab'}}>Day Range</small>
                    <div className="fw-semibold" style={{fontSize: '0.8rem', color: darkTheme ? '#c7d2fe' : '#1a237e'}}>${parseFloat(stockData.day_low).toFixed(2)} - ${parseFloat(stockData.day_high).toFixed(2)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Historical Data Boxes - 1 Week, 2 Weeks, 1 Month */}
      {historicalData && activeTab === 'overview' && (
        <div className="mt-3 mt-md-4" style={{width:'100%', maxWidth:'100%', overflowX:'hidden'}}>
          <h2 className="h6 h5-md mb-2 mb-md-3 fw-bold px-2 px-md-3" style={{color: darkTheme ? '#60a5fa' : '#0d6efd'}}>Historical Performance</h2>
          <div className="row g-2 g-md-3 px-2 px-md-3">
            {/* 1 Week Card */}
            <div className="col-12 col-md-4">
              <div className="card shadow-sm border-start border-3" style={{
                backgroundColor: darkTheme ? '#334155' : '#fff',
                borderColor: darkTheme ? '#22c55e !important' : '#198754',
                borderLeftColor: darkTheme ? '#22c55e' : '#198754'
              }}>
                <div className="card-header py-2" style={{
                  backgroundColor: darkTheme ? 'rgba(34, 197, 94, 0.15)' : 'rgba(25, 135, 84, 0.1)'
                }}>
                  <h3 className="mb-0 fw-bold" style={{fontSize: '0.9rem', color: darkTheme ? '#4ade80' : '#198754'}}>1 Week</h3>
                  {historicalData.week && historicalData.week.start_date && (
                    <small className="d-block mt-1" style={{fontSize: '0.65rem', color: darkTheme ? '#94a3b8' : '#6c757d'}}>
                      {historicalData.week.start_date} to {historicalData.week.end_date}
                    </small>
                  )}
                </div>
                <div className="card-body py-2 px-2" style={{backgroundColor: darkTheme ? '#334155' : '#fff'}}>
                  {historicalData.week ? (
                    <div style={{fontSize: '0.8rem'}}>
                      <div className="d-flex justify-content-between mb-1">
                        <span style={{color: darkTheme ? '#94a3b8' : '#6c757d'}}>High:</span>
                        <span className="fw-semibold" style={{color: darkTheme ? '#e2e8f0' : '#212529'}}>${historicalData.week.high}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span style={{color: darkTheme ? '#94a3b8' : '#6c757d'}}>Low:</span>
                        <span className="fw-semibold" style={{color: darkTheme ? '#e2e8f0' : '#212529'}}>${historicalData.week.low}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span style={{color: darkTheme ? '#94a3b8' : '#6c757d'}}>Avg Price:</span>
                        <span className="fw-semibold" style={{color: darkTheme ? '#e2e8f0' : '#212529'}}>${historicalData.week.avg_price}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-secondary">Change:</span>
                        <span className={`fw-bold ${historicalData.week.price_change >= 0 ? 'text-success' : 'text-danger'}`}>
                          {historicalData.week.price_change >= 0 ? '+' : ''}${historicalData.week.price_change} ({historicalData.week.price_change_percent}%)
                        </span>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-secondary">Volatility:</span>
                        <span className="fw-semibold">${historicalData.week.volatility}</span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-secondary">Avg Volume:</span>
                        <span className="fw-semibold">{formatLargeNumber(historicalData.week.avg_volume)}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted mb-0">No data available</p>
                  )}
                </div>
              </div>
            </div>

            {/* 2 Weeks Card */}
            <div className="col-12 col-md-4">
              <div className="card shadow-sm border-start border-warning border-3">
                <div className="card-header bg-warning bg-opacity-10 py-2">
                  <h3 className="mb-0 fw-bold text-warning" style={{fontSize: '0.9rem'}}>2 Weeks</h3>
                  {historicalData.two_weeks && historicalData.two_weeks.start_date && (
                    <small className="text-muted d-block mt-1" style={{fontSize: '0.65rem'}}>
                      {historicalData.two_weeks.start_date} to {historicalData.two_weeks.end_date}
                    </small>
                  )}
                </div>
                <div className="card-body py-2 px-2">
                  {historicalData.two_weeks ? (
                    <div style={{fontSize: '0.8rem'}}>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-secondary">High:</span>
                        <span className="fw-semibold">${historicalData.two_weeks.high}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-secondary">Low:</span>
                        <span className="fw-semibold">${historicalData.two_weeks.low}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-secondary">Avg Price:</span>
                        <span className="fw-semibold">${historicalData.two_weeks.avg_price}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-secondary">Change:</span>
                        <span className={`fw-bold ${historicalData.two_weeks.price_change >= 0 ? 'text-success' : 'text-danger'}`}>
                          {historicalData.two_weeks.price_change >= 0 ? '+' : ''}${historicalData.two_weeks.price_change} ({historicalData.two_weeks.price_change_percent}%)
                        </span>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-secondary">Volatility:</span>
                        <span className="fw-semibold">${historicalData.two_weeks.volatility}</span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-secondary">Avg Volume:</span>
                        <span className="fw-semibold">{formatLargeNumber(historicalData.two_weeks.avg_volume)}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted mb-0">No data available</p>
                  )}
                </div>
              </div>
            </div>

            {/* 1 Month Card */}
            <div className="col-12 col-md-4">
              <div className="card shadow-sm border-start border-danger border-3">
                <div className="card-header bg-danger bg-opacity-10 py-2">
                  <h3 className="mb-0 fw-bold text-danger" style={{fontSize: '0.9rem'}}>1 Month</h3>
                  {historicalData.month && historicalData.month.start_date && (
                    <small className="text-muted d-block mt-1" style={{fontSize: '0.65rem'}}>
                      {historicalData.month.start_date} to {historicalData.month.end_date}
                    </small>
                  )}
                </div>
                <div className="card-body py-2 px-2">
                  {historicalData.month ? (
                    <div style={{fontSize: '0.8rem'}}>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-secondary">High:</span>
                        <span className="fw-semibold">${historicalData.month.high}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-secondary">Low:</span>
                        <span className="fw-semibold">${historicalData.month.low}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-secondary">Avg Price:</span>
                        <span className="fw-semibold">${historicalData.month.avg_price}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-secondary">Change:</span>
                        <span className={`fw-bold ${historicalData.month.price_change >= 0 ? 'text-success' : 'text-danger'}`}>
                          {historicalData.month.price_change >= 0 ? '+' : ''}${historicalData.month.price_change} ({historicalData.month.price_change_percent}%)
                        </span>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-secondary">Volatility:</span>
                        <span className="fw-semibold">${historicalData.month.volatility}</span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-secondary">Avg Volume:</span>
                        <span className="fw-semibold">{formatLargeNumber(historicalData.month.avg_volume)}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted mb-0">No data available</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Price Chart */}
      {historicalData && historicalData.chart_data && historicalData.chart_data.length > 0 && activeTab === 'charts' && (
        <div className="mt-3 mt-md-4" style={{width:'100%', maxWidth:'100%', overflowX:'hidden'}}>
          <div className="card shadow-sm border border-primary-subtle mx-2 mx-md-3">
            <div className="card-header bg-primary text-white bg-gradient">
              <h2 className="h6 h5-md mb-0">{selectedStock} Price Chart (Last 30 Days)</h2>
            </div>
            <div className="card-body p-2 p-md-3">
              <ResponsiveContainer width="100%" height={300} className="d-md-none">
                <LineChart data={historicalData.chart_data} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 9 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis 
                    domain={['auto', 'auto']}
                    tick={{ fontSize: 9 }}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', fontSize: '0.75rem' }}
                    formatter={(value) => `$${value}`}
                  />
                  <Legend wrapperStyle={{ fontSize: '0.7rem' }} />
                  <Line type="monotone" dataKey="close" stroke="#0d6efd" strokeWidth={2} name="Close" dot={false} />
                  <Line type="monotone" dataKey="high" stroke="#198754" strokeWidth={1} name="High" dot={false} strokeDasharray="5 5" />
                  <Line type="monotone" dataKey="low" stroke="#dc3545" strokeWidth={1} name="Low" dot={false} strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
              <ResponsiveContainer width="100%" height={400} className="d-none d-md-block">
                <LineChart data={historicalData.chart_data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    domain={['auto', 'auto']}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}
                    formatter={(value) => `$${value}`}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="close" stroke="#0d6efd" strokeWidth={2} name="Close Price" dot={false} />
                  <Line type="monotone" dataKey="high" stroke="#198754" strokeWidth={1} name="High" dot={false} strokeDasharray="5 5" />
                  <Line type="monotone" dataKey="low" stroke="#dc3545" strokeWidth={1} name="Low" dot={false} strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* One Year Price Chart */}
      {historicalData && historicalData.chart_data_year && historicalData.chart_data_year.length > 0 && activeTab === 'charts' && (
        <div className="mt-3 mt-md-4" style={{width:'100%', maxWidth:'100%', overflowX:'hidden'}}>
          <div className="card shadow-sm border border-success-subtle mx-2 mx-md-3">
            <div className="card-header bg-success text-white bg-gradient">
              <h2 className="h6 h5-md mb-0">{selectedStock} Price Chart (Last 1 Year)</h2>
            </div>
            <div className="card-body p-2 p-md-3">
              <ResponsiveContainer width="100%" height={300} className="d-md-none">
                <LineChart data={historicalData.chart_data_year} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 9 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    interval="preserveStartEnd"
                  />
                  <YAxis 
                    domain={['auto', 'auto']}
                    tick={{ fontSize: 9 }}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', fontSize: '0.75rem' }}
                    formatter={(value) => `$${value}`}
                  />
                  <Legend wrapperStyle={{ fontSize: '0.7rem' }} />
                  <Line type="monotone" dataKey="close" stroke="#198754" strokeWidth={2} name="Close" dot={false} />
                  <Line type="monotone" dataKey="high" stroke="#0d6efd" strokeWidth={1} name="High" dot={false} strokeDasharray="5 5" />
                  <Line type="monotone" dataKey="low" stroke="#dc3545" strokeWidth={1} name="Low" dot={false} strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
              <ResponsiveContainer width="100%" height={450} className="d-none d-md-block">
                <LineChart data={historicalData.chart_data_year} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 11 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval="preserveStartEnd"
                  />
                  <YAxis 
                    domain={['auto', 'auto']}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}
                    formatter={(value) => `$${value}`}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="close" stroke="#198754" strokeWidth={2} name="Close Price" dot={false} />
                  <Line type="monotone" dataKey="high" stroke="#0d6efd" strokeWidth={1} name="High" dot={false} strokeDasharray="5 5" />
                  <Line type="monotone" dataKey="low" stroke="#dc3545" strokeWidth={1} name="Low" dot={false} strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Full-width AI Analysis section below */}
      {stockData && !error && aiAnalysis && activeTab === 'ai-analysis' && (
        <div className="mt-4" style={{width:'100%', maxWidth:'100%', overflowX:'hidden'}}>
          <div className="border border-purple-200 rounded shadow-lg p-3 p-md-4 mx-2 mx-md-3" style={{overflowX:'hidden', background: '#fff'}}>
            <h2 className="h4 fw-bold mb-4 text-center p-3 rounded" style={{
              background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)'
            }}>
              <span style={{fontSize: '2rem'}}>🤖</span>
              <span style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginLeft: '0.5rem'
              }}>
                Swing Trade Advisor
              </span>
            </h2>
            
            {/* Sub-tabs for AI Analysis */}
            <ul className="nav nav-pills mb-4 justify-content-center" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${aiSubTab === 'sections' ? 'active' : ''}`}
                  onClick={() => setAiSubTab('sections')}
                  type="button"
                  role="tab"
                  style={{fontSize: '0.9rem'}}
                >
                  Section View
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${aiSubTab === 'formatted' ? 'active' : ''}`}
                  onClick={() => setAiSubTab('formatted')}
                  type="button"
                  role="tab"
                  style={{fontSize: '0.9rem'}}
                >
                  Plain View
                </button>
              </li>
            </ul>

            {/* Formatted Analysis Sub-tab */}
            {aiSubTab === 'formatted' && (
              <div className="text-wrap text-break" style={{maxWidth:'100%'}}>
                {parseAiAnalysis(aiAnalysis) || (
                  <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere', maxWidth: '100%' }}>
                    {aiAnalysis.replace(/\*\*\*/g, '').replace(/\*\*/g, '').replace(/\*/g, '')}
                  </pre>
                )}
              </div>
            )}

            {/* Section View Sub-tab */}
            {aiSubTab === 'sections' && (
              <div className="row g-3">
                {(() => {
                  const sections = [
                    {
                      title: 'Current Trend Analysis',
                      color: '#3b82f6',
                      bgColor: '#eff6ff',
                      key: 'current_trend_analysis'
                    },
                    {
                      title: 'Key Support and Resistance Levels',
                      color: '#4ade80',
                      bgColor: '#dcfce7',
                      key: 'key_support_resistance_levels'
                    },
                    {
                      title: 'Potential Swing Trading Opportunities',
                      color: '#10b981',
                      bgColor: '#f0fdf4',
                      key: 'potential_swing_trading_opportunities'
                    },
                    {
                      title: 'Risk Assessment for Swing Trading',
                      color: '#f59e0b',
                      bgColor: '#fffbeb',
                      key: 'risk_assessment'
                    },
                    {
                      title: 'Recommended Timeframe for Swing Trades',
                      color: '#ec4899',
                      bgColor: '#fdf2f8',
                      key: 'recommended_timeframe'
                    },
                    {
                      title: 'Prediction for the Stock in Next Two Weeks',
                      color: '#14b8a6',
                      bgColor: '#f0fdfa',
                      key: 'two_week_prediction'
                    }
                  ];

                  return sections.map((section, index) => {
                    const content = aiAnalysisStructured ? aiAnalysisStructured[section.key] : '';
                    const displayContent = content || 'No information available for this section.';

                    return (
                      <div key={index} className="col-12 col-md-6">
                        <div className="card h-100 shadow-sm" style={{borderLeft: `4px solid ${section.color}`}}>
                          <div className="card-header" style={{
                            background: `linear-gradient(135deg, ${section.color} 0%, ${section.color}dd 100%)`,
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.95rem'
                          }}>
                            {section.title}
                          </div>
                          <div className="card-body" style={{backgroundColor: section.bgColor}}>
                            <p className="mb-0" style={{
                              fontSize: '0.9rem',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              lineHeight: '1.6'
                            }}>
                              {displayContent.replace(/\*\*\*/g, '').replace(/\*\*/g, '').replace(/\*/g, '').replace(/^\s*[-•]\s*/gm, '• ')}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}

            {/* Disclaimer at bottom */}
            <div className="mt-4 pt-3 border-top" style={{backgroundColor: '#f8f9fa', borderRadius: '5px', padding: '1rem'}}>
              <h6 className="text-muted mb-2" style={{fontSize: '0.85rem', fontWeight: '600'}}>
                <span className="me-2">⚠️</span>Disclaimer
              </h6>
              <p className="text-muted mb-0" style={{fontSize: '0.75rem', lineHeight: '1.5'}}>
                This application is intended for educational and analytical purposes only. The information provided is based on data and algorithms designed to assist in trade analysis but should not be construed as financial advice. Users are encouraged to consult with a qualified financial advisor or expert before making any investment decisions. The use of this application is at your own risk, and Swing Trade Analyzer will not be held liable for any losses or risks incurred from trades or investment decisions made based on the information provided.
              </p>
            </div>
          </div>
        </div>
      )}

      {stockData && !error && dayAiAnalysis && activeTab === 'day-ai-analysis' && (
        <div className="mt-4" style={{width:'100%', maxWidth:'100%', overflowX:'hidden'}}>
          <div className="border border-orange-200 rounded shadow-lg p-3 p-md-4 mx-2 mx-md-3" style={{overflowX:'hidden', background: '#fff'}}>
            <h2 className="h4 fw-bold mb-4 text-center p-3 rounded" style={{
              background: 'linear-gradient(135deg, #ffedd5 0%, #fed7aa 100%)'
            }}>
              <span style={{fontSize: '2rem'}}>⚡</span>
              <span style={{
                background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginLeft: '0.5rem'
              }}>
                AI Day Trading Analysis
              </span>
            </h2>

            <ul className="nav nav-pills mb-4 justify-content-center" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${dayAiSubTab === 'sections' ? 'active' : ''}`}
                  onClick={() => setDayAiSubTab('sections')}
                  type="button"
                  role="tab"
                  style={{fontSize: '0.9rem'}}
                >
                  Section View
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${dayAiSubTab === 'formatted' ? 'active' : ''}`}
                  onClick={() => setDayAiSubTab('formatted')}
                  type="button"
                  role="tab"
                  style={{fontSize: '0.9rem'}}
                >
                  Plain View
                </button>
              </li>
            </ul>

            {dayAiSubTab === 'formatted' && (
              <div className="text-wrap text-break" style={{maxWidth:'100%'}}>
                {parseAiAnalysis(dayAiAnalysis) || (
                  <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere', maxWidth: '100%' }}>
                    {dayAiAnalysis.replace(/\*\*\*/g, '').replace(/\*\*/g, '').replace(/\*/g, '')}
                  </pre>
                )}
              </div>
            )}

            {dayAiSubTab === 'sections' && (
              <div className="row g-3">
                {(() => {
                  const sections = [
                    { title: 'Current Trend Analysis', color: '#ea580c', bgColor: '#fff7ed', key: 'current_trend_analysis' },
                    { title: 'Key Support and Resistance Levels', color: '#f97316', bgColor: '#ffedd5', key: 'key_support_resistance_levels' },
                    { title: 'Potential Day Trading Opportunities', color: '#fb923c', bgColor: '#fff7ed', key: 'potential_swing_trading_opportunities' },
                    { title: 'Risk Assessment for Day Trading', color: '#ef4444', bgColor: '#fef2f2', key: 'risk_assessment' },
                    { title: 'Recommended Timeframe for Day Trades', color: '#d97706', bgColor: '#fffbeb', key: 'recommended_timeframe' },
                    { title: 'Prediction for the Stock by Market Close', color: '#c2410c', bgColor: '#fff7ed', key: 'two_week_prediction' }
                  ];

                  return sections.map((section, index) => {
                    const content = dayAiAnalysisStructured ? dayAiAnalysisStructured[section.key] : '';
                    const displayContent = content || 'No information available for this section.';

                    return (
                      <div key={index} className="col-12 col-md-6">
                        <div className="card h-100 shadow-sm" style={{borderLeft: `4px solid ${section.color}`}}>
                          <div className="card-header" style={{
                            background: `linear-gradient(135deg, ${section.color} 0%, ${section.color}dd 100%)`,
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.95rem'
                          }}>
                            {section.title}
                          </div>
                          <div className="card-body" style={{backgroundColor: section.bgColor}}>
                            <p className="mb-0" style={{
                              fontSize: '0.9rem',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              lineHeight: '1.6'
                            }}>
                              {displayContent.replace(/\*\*\*/g, '').replace(/\*\*/g, '').replace(/\*/g, '').replace(/^\s*[-•]\s*/gm, '• ')}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}

            {/* Disclaimer at bottom */}
            <div className="mt-4 pt-3 border-top" style={{backgroundColor: '#f8f9fa', borderRadius: '5px', padding: '1rem'}}>
              <h6 className="text-muted mb-2" style={{fontSize: '0.85rem', fontWeight: '600'}}>
                <span className="me-2">⚠️</span>Disclaimer
              </h6>
              <p className="text-muted mb-0" style={{fontSize: '0.75rem', lineHeight: '1.5'}}>
                This application is intended for educational and analytical purposes only. The information provided is based on data and algorithms designed to assist in trade analysis but should not be construed as financial advice. Users are encouraged to consult with a qualified financial advisor or expert before making any investment decisions. The use of this application is at your own risk, and Swing Trade Analyzer will not be held liable for any losses or risks incurred from trades or investment decisions made based on the information provided.
              </p>
            </div>
          </div>
        </div>
      )}

      {stockData && !error && centimentAnalysis && activeTab === 'centiment-ai-analysis' && (
        <div className="mt-4" style={{width:'100%', maxWidth:'100%', overflowX:'hidden'}}>
          <div className="border rounded shadow-lg p-3 p-md-4 mx-2 mx-md-3" style={{overflowX:'hidden', background: '#fff', borderColor: '#99f6e4'}}>
            <h2 className="h4 fw-bold mb-4 text-center p-3 rounded" style={{
              background: 'linear-gradient(135deg, #ccfbf1 0%, #99f6e4 100%)'
            }}>
              <span style={{fontSize: '2rem'}}>🤖</span>
              <span style={{
                background: 'linear-gradient(135deg, #0f766e 0%, #115e59 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginLeft: '0.5rem'
              }}>
                Centiment Advisor
              </span>
            </h2>

            <ul className="nav nav-pills mb-4 justify-content-center" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${centimentSubTab === 'sections' ? 'active' : ''}`}
                  onClick={() => setCentimentSubTab('sections')}
                  type="button"
                  role="tab"
                  style={{fontSize: '0.9rem'}}
                >
                  Section View
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${centimentSubTab === 'formatted' ? 'active' : ''}`}
                  onClick={() => setCentimentSubTab('formatted')}
                  type="button"
                  role="tab"
                  style={{fontSize: '0.9rem'}}
                >
                  Plain View
                </button>
              </li>
            </ul>

            {centimentSubTab === 'formatted' && (
              <div className="text-wrap text-break" style={{maxWidth:'100%'}}>
                {parseAiAnalysis(centimentAnalysis) || (
                  <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere', maxWidth: '100%' }}>
                    {centimentAnalysis.replace(/\*\*\*/g, '').replace(/\*\*/g, '').replace(/\*/g, '')}
                  </pre>
                )}
              </div>
            )}

            {centimentSubTab === 'sections' && (
              <div className="row g-3">
                {(() => {
                  const sections = [
                    { title: 'Overall Market Sentiment', color: '#0f766e', bgColor: '#f0fdfa', key: 'overall_market_sentiment' },
                    { title: 'News and Narrative Drivers', color: '#14b8a6', bgColor: '#f0fdfa', key: 'news_and_narrative_drivers' },
                    { title: 'Investor Emotion Signals', color: '#0d9488', bgColor: '#ecfeff', key: 'investor_emotion_signals' },
                    { title: 'Potential Price Reaction (Near Term)', color: '#0284c7', bgColor: '#eff6ff', key: 'potential_price_reaction' },
                    { title: 'Sentiment Risk Factors', color: '#dc2626', bgColor: '#fef2f2', key: 'sentiment_risk_factors' },
                    { title: 'Centiment Advisor Outlook', color: '#115e59', bgColor: '#f0fdfa', key: 'centiment_advisor_outlook' }
                  ];

                  return sections.map((section, index) => {
                    const content = centimentAnalysisStructured ? centimentAnalysisStructured[section.key] : '';
                    const displayContent = content || 'No information available for this section.';

                    return (
                      <div key={index} className="col-12 col-md-6">
                        <div className="card h-100 shadow-sm" style={{borderLeft: `4px solid ${section.color}`}}>
                          <div className="card-header" style={{
                            background: `linear-gradient(135deg, ${section.color} 0%, ${section.color}dd 100%)`,
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.95rem'
                          }}>
                            {section.title}
                          </div>
                          <div className="card-body" style={{backgroundColor: section.bgColor}}>
                            <p className="mb-0" style={{
                              fontSize: '0.9rem',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              lineHeight: '1.6'
                            }}>
                              {displayContent.replace(/\*\*\*/g, '').replace(/\*\*/g, '').replace(/\*/g, '').replace(/^\s*[-•]\s*/gm, '• ')}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}

            {/* Disclaimer at bottom */}
            <div className="mt-4 pt-3 border-top" style={{backgroundColor: '#f8f9fa', borderRadius: '5px', padding: '1rem'}}>
              <h6 className="text-muted mb-2" style={{fontSize: '0.85rem', fontWeight: '600'}}>
                <span className="me-2">⚠️</span>Disclaimer
              </h6>
              <p className="text-muted mb-0" style={{fontSize: '0.75rem', lineHeight: '1.5'}}>
                This application is intended for educational and analytical purposes only. The information provided is based on data and algorithms designed to assist in trade analysis but should not be construed as financial advice. Users are encouraged to consult with a qualified financial advisor or expert before making any investment decisions. The use of this application is at your own risk, and Swing Trade Analyzer will not be held liable for any losses or risks incurred from trades or investment decisions made based on the information provided.
              </p>
            </div>
          </div>
        </div>
      )}

      {stockData && !error && phaseOneAdvisors && activeTab === 'phase1-advisors' && (
        <div className="mt-4" style={{width:'100%', maxWidth:'100%', overflowX:'hidden'}}>
          <div className="border rounded shadow-lg p-3 p-md-4 mx-2 mx-md-3" style={{overflowX:'hidden', background: '#fff', borderColor: '#67e8f9'}}>
            <h2 className="h4 fw-bold mb-4 text-center p-3 rounded" style={{
              background: 'linear-gradient(135deg, #cffafe 0%, #a5f3fc 100%)'
            }}>
              <span style={{fontSize: '2rem'}}>📐</span>
              <span style={{
                background: 'linear-gradient(135deg, #0e7490 0%, #155e75 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginLeft: '0.5rem'
              }}>
                Trade Setup Tools
              </span>
            </h2>

            <div className="row g-3">
              <div className="col-12 col-lg-4">
                <div className="card h-100 shadow-sm" style={{borderLeft: '4px solid #0ea5e9'}}>
                  <div className="card-header text-white" style={{background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)', fontWeight: 'bold'}}>
                    Position Sizing Advisor
                  </div>
                  <div className="card-body" style={{backgroundColor: '#f0f9ff'}}>
                    <div className="mb-2 p-2 rounded" style={{
                      background: '#ecfeff',
                      border: '1px solid #a5f3fc'
                    }}>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span style={{fontSize: '0.75rem', fontWeight: 600, color: '#0e7490'}}>
                          Position Sizing Inputs
                        </span>
                        <div className="d-flex gap-1">
                          {phaseOnePositionSizingLoading && (
                            <span style={{fontSize: '0.68rem', color: '#0369a1', alignSelf: 'center'}}>Updating...</span>
                          )}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            style={{fontSize: '0.68rem', padding: '0.1rem 0.4rem'}}
                            disabled={isLoading || phaseOneAdvisorsLoading || phaseOnePositionSizingLoading}
                            onClick={handleRecalculatePhaseOne}
                          >
                            Recalculate
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            style={{fontSize: '0.68rem', padding: '0.1rem 0.4rem'}}
                            onClick={() => {
                              setPhaseOneCapital('10000');
                              setPhaseOneRiskPct('1');
                              setPhaseOneStopPct('3');
                            }}
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                      <div className="row g-2">
                        <div className="col-12">
                          <label className="form-label mb-1" style={{fontSize: '0.7rem'}}>Capital</label>
                          <input
                            type="number"
                            min="100"
                            step="100"
                            className="form-control form-control-sm"
                            value={phaseOneCapital}
                            onChange={(e) => setPhaseOneCapital(e.target.value)}
                            disabled={isLoading || phaseOneAdvisorsLoading}
                          />
                        </div>
                        <div className="col-6">
                          <label className="form-label mb-1" style={{fontSize: '0.7rem'}}>Risk %</label>
                          <input
                            type="number"
                            min="0.1"
                            max="5"
                            step="0.1"
                            className="form-control form-control-sm"
                            value={phaseOneRiskPct}
                            onChange={(e) => setPhaseOneRiskPct(e.target.value)}
                            disabled={isLoading || phaseOneAdvisorsLoading}
                          />
                        </div>
                        <div className="col-6">
                          <label className="form-label mb-1" style={{fontSize: '0.7rem'}}>Stop %</label>
                          <input
                            type="number"
                            min="0.3"
                            max="20"
                            step="0.1"
                            className="form-control form-control-sm"
                            value={phaseOneStopPct}
                            onChange={(e) => setPhaseOneStopPct(e.target.value)}
                            disabled={isLoading || phaseOneAdvisorsLoading}
                          />
                        </div>
                      </div>
                      <div className="mt-1" style={{fontSize: '0.68rem', color: '#0f766e'}}>
                        Calculations update automatically while you type. Use Recalculate if you want an immediate manual refresh.
                      </div>
                    </div>
                    <p className="mb-2"><strong>Recommended Shares:</strong> {phaseOneAdvisors.positionSizing?.plan?.recommended_shares ?? 'N/A'}</p>
                    <p className="mb-2"><strong>Position Value:</strong> ${phaseOneAdvisors.positionSizing?.plan?.position_value ?? 'N/A'}</p>
                    <p className="mb-2"><strong>Max Loss:</strong> ${phaseOneAdvisors.positionSizing?.plan?.max_loss ?? 'N/A'}</p>
                    <p className="mb-2"><strong>Risk/Share:</strong> ${phaseOneAdvisors.positionSizing?.plan?.risk_per_share ?? 'N/A'}</p>
                    <details open>
                      <summary style={{cursor: 'pointer', fontWeight: 600}}>How this was calculated</summary>
                      <div className="small mt-2">
                        <p className="mb-1"><strong>Entry Price:</strong> ${phaseOneAdvisors.positionSizing?.inputs?.entry_price ?? 'N/A'}</p>
                        <p className="mb-1"><strong>Risk %:</strong> {phaseOneAdvisors.positionSizing?.inputs?.risk_pct ?? 'N/A'}%</p>
                        <p className="mb-1"><strong>Stop %:</strong> {phaseOneAdvisors.positionSizing?.inputs?.stop_pct ?? 'N/A'}%</p>
                        <p className="mb-1"><strong>Stop Price:</strong> ${phaseOneAdvisors.positionSizing?.plan?.stop_price ?? 'N/A'}</p>
                        <p className="mb-1"><strong>Position Size:</strong> {phaseOneAdvisors.positionSizing?.plan?.position_size_pct_of_capital ?? 'N/A'}% of capital</p>
                        <p className="mb-2"><strong>Cash Remaining:</strong> ${phaseOneAdvisors.positionSizing?.plan?.cash_remaining ?? 'N/A'}</p>
                        <ol className="mb-2 ps-3">
                          <li>{phaseOneAdvisors.positionSizing?.calculation_breakdown?.step_1 ?? 'N/A'}</li>
                          <li>{phaseOneAdvisors.positionSizing?.calculation_breakdown?.step_2 ?? 'N/A'}</li>
                          <li>{phaseOneAdvisors.positionSizing?.calculation_breakdown?.step_3 ?? 'N/A'}</li>
                          <li>{phaseOneAdvisors.positionSizing?.calculation_breakdown?.step_4 ?? 'N/A'}</li>
                          <li>{phaseOneAdvisors.positionSizing?.calculation_breakdown?.step_5 ?? 'N/A'}</li>
                        </ol>
                        <p className="mb-1"><strong>Summary:</strong> {phaseOneAdvisors.positionSizing?.plain_english?.summary ?? 'N/A'}</p>
                        <p className="mb-0"><strong>Rule:</strong> {phaseOneAdvisors.positionSizing?.plain_english?.rule_of_thumb ?? 'N/A'}</p>
                      </div>
                    </details>
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-4">
                <div className="card h-100 shadow-sm" style={{borderLeft: '4px solid #f97316'}}>
                  <div className="card-header text-white" style={{background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', fontWeight: 'bold'}}>
                    Stop-Loss Optimization Advisor
                  </div>
                  <div className="card-body" style={{backgroundColor: '#fff7ed'}}>
                    <p className="mb-2"><strong>Mode:</strong> {phaseOneAdvisors.stopLoss?.mode ?? 'N/A'}</p>
                    <p className="mb-2"><strong>Selected Stop:</strong> ${phaseOneAdvisors.stopLoss?.selected_stop ?? 'N/A'}</p>
                    <p className="mb-2"><strong>Distance:</strong> {phaseOneAdvisors.stopLoss?.distance_pct ?? 'N/A'}%</p>
                    <p className="mb-2"><strong>Recommended:</strong> {phaseOneAdvisors.stopLoss?.recommended_mode ?? 'N/A'}</p>
                    <details open>
                      <summary style={{cursor: 'pointer', fontWeight: 600}}>More details</summary>
                      <div className="small mt-2">
                        <p className="mb-1"><strong>Entry Price:</strong> ${phaseOneAdvisors.stopLoss?.entry_price ?? 'N/A'}</p>
                        <p className="mb-1"><strong>Risk Per Share (Entry - Stop):</strong> ${(
                          phaseOneAdvisors.stopLoss?.entry_price != null && phaseOneAdvisors.stopLoss?.selected_stop != null
                            ? Math.max(0, Number(phaseOneAdvisors.stopLoss.entry_price) - Number(phaseOneAdvisors.stopLoss.selected_stop)).toFixed(2)
                            : 'N/A'
                        )}</p>
                        <p className="mb-2"><strong>Estimated Loss at Current Sized Shares:</strong> ${(
                          phaseOneAdvisors.positionSizing?.plan?.recommended_shares != null && phaseOneAdvisors.stopLoss?.entry_price != null && phaseOneAdvisors.stopLoss?.selected_stop != null
                            ? (
                                Number(phaseOneAdvisors.positionSizing.plan.recommended_shares) *
                                Math.max(0, Number(phaseOneAdvisors.stopLoss.entry_price) - Number(phaseOneAdvisors.stopLoss.selected_stop))
                              ).toFixed(2)
                            : 'N/A'
                        )}</p>

                        <div className="table-responsive mb-2">
                          <table className="table table-sm mb-0">
                            <thead>
                              <tr>
                                <th>Stop Type</th>
                                <th className="text-end">Price</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>Fixed (3%)</td>
                                <td className="text-end">${phaseOneAdvisors.stopLoss?.stops?.fixed ?? 'N/A'}</td>
                              </tr>
                              <tr>
                                <td>ATR-based</td>
                                <td className="text-end">${phaseOneAdvisors.stopLoss?.stops?.atr ?? 'N/A'}</td>
                              </tr>
                              <tr>
                                <td>Structure</td>
                                <td className="text-end">${phaseOneAdvisors.stopLoss?.stops?.structure ?? 'N/A'}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        <p className="mb-1"><strong>Mode Guide:</strong></p>
                        <ul className="mb-2 ps-3">
                          <li><strong>Fixed:</strong> Simple and consistent, best when volatility is low.</li>
                          <li><strong>ATR:</strong> Wider stop that adapts to volatility.</li>
                          <li><strong>Structure:</strong> Uses nearby support zones to reduce noise exits.</li>
                        </ul>

                        <p className="mb-1"><strong>Advisor Notes:</strong></p>
                        <ul className="mb-0 ps-3">
                          {(phaseOneAdvisors.stopLoss?.notes || []).map((note, idx) => (
                            <li key={`stoploss-note-${idx}`}>{note}</li>
                          ))}
                        </ul>
                      </div>
                    </details>
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-4">
                <div className="card h-100 shadow-sm" style={{borderLeft: '4px solid #10b981'}}>
                  <div className="card-header text-white" style={{background: 'linear-gradient(135deg, #10b981 0%, #047857 100%)', fontWeight: 'bold'}}>
                    Entry / Exit Timing Advisor
                  </div>
                  <div className="card-body" style={{backgroundColor: '#f0fdf4'}}>
                    <p className="mb-2"><strong>Entry Zone:</strong> ${phaseOneAdvisors.entryExit?.entry_zone?.low ?? 'N/A'} - ${phaseOneAdvisors.entryExit?.entry_zone?.high ?? 'N/A'}</p>
                    <p className="mb-2"><strong>Targets:</strong> {phaseOneAdvisors.entryExit?.targets ? phaseOneAdvisors.entryExit.targets.map((target) => `$${target}`).join(', ') : 'N/A'}</p>
                    <p className="mb-2"><strong>Invalidation:</strong> ${phaseOneAdvisors.entryExit?.invalidation ?? 'N/A'}</p>
                    <p className="mb-2"><strong>Timeframe:</strong> {phaseOneAdvisors.entryExit?.timeframe ?? 'N/A'}</p>
                    <details open>
                      <summary style={{cursor: 'pointer', fontWeight: 600}}>More details</summary>
                      <div className="small mt-2">
                        <p className="mb-1"><strong>Trading Style:</strong> {(phaseOneAdvisors.entryExit?.style || 'N/A').toUpperCase()}</p>
                        <p className="mb-1"><strong>Suggested Buy Area:</strong> Lower half of the entry zone for better reward/risk.</p>
                        <p className="mb-2"><strong>Invalidation Rule:</strong> Exit if price closes below invalidation level.</p>

                        <div className="table-responsive mb-2">
                          <table className="table table-sm mb-0">
                            <thead>
                              <tr>
                                <th>Target</th>
                                <th className="text-end">Price</th>
                                <th className="text-end">R/R (approx)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                const entryLow = Number(phaseOneAdvisors.entryExit?.entry_zone?.low);
                                const entryHigh = Number(phaseOneAdvisors.entryExit?.entry_zone?.high);
                                const invalidation = Number(phaseOneAdvisors.entryExit?.invalidation);
                                const riskPerShare = Number.isFinite(entryLow) && Number.isFinite(invalidation)
                                  ? Math.max(entryLow - invalidation, 0.0001)
                                  : null;
                                const avgEntry = Number.isFinite(entryLow) && Number.isFinite(entryHigh)
                                  ? (entryLow + entryHigh) / 2
                                  : null;
                                const targets = phaseOneAdvisors.entryExit?.targets || [];

                                return targets.map((t, idx) => {
                                  const targetNum = Number(t);
                                  const rr = (riskPerShare && Number.isFinite(targetNum) && Number.isFinite(avgEntry))
                                    ? ((targetNum - avgEntry) / riskPerShare)
                                    : null;
                                  return (
                                    <tr key={`entry-exit-rr-${idx}`}>
                                      <td>{`T${idx + 1}`}</td>
                                      <td className="text-end">${Number.isFinite(targetNum) ? targetNum.toFixed(2) : 'N/A'}</td>
                                      <td className="text-end">{rr != null ? `${rr.toFixed(2)}R` : 'N/A'}</td>
                                    </tr>
                                  );
                                });
                              })()}
                            </tbody>
                          </table>
                        </div>

                        <p className="mb-1"><strong>Execution Plan:</strong></p>
                        <ol className="mb-2 ps-3">
                          <li>Enter in the zone when price confirms support/strength.</li>
                          <li>Take partial profits near Target 1.</li>
                          <li>Trail stop toward breakeven between Target 1 and Target 2.</li>
                          <li>Let remaining size run toward Target 3 if momentum holds.</li>
                        </ol>

                        <p className="mb-1"><strong>Advisor Notes:</strong></p>
                        <ul className="mb-0 ps-3">
                          {(phaseOneAdvisors.entryExit?.notes || []).map((note, idx) => (
                            <li key={`entry-exit-note-${idx}`}>{note}</li>
                          ))}
                        </ul>
                      </div>
                    </details>
                  </div>
                </div>
              </div>
            </div>

            <div className="alert alert-warning py-2 mt-3 mb-0" style={{ fontSize: '0.8rem' }}>
              Disclaimer: Trade Setup outputs are model-based guidance for education only, not financial advice. Verify with your own research before placing trades.
            </div>
          </div>
        </div>
      )}

      {stockData && !error && phaseTwoAdvisors && activeTab === 'phase2-advisors' && (
        <div className="mt-4" style={{width:'100%', maxWidth:'100%', overflowX:'hidden'}}>
          <div className="border rounded shadow-lg p-3 p-md-4 mx-2 mx-md-3" style={{overflowX:'hidden', background: '#fff', borderColor: '#c4b5fd'}}>
            <h2 className="h4 fw-bold mb-4 text-center p-3 rounded" style={{
              background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)'
            }}>
              <span style={{fontSize: '2rem'}}>🧭</span>
              <span style={{
                background: 'linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginLeft: '0.5rem'
              }}>
                Regime, Momentum & Rotation
              </span>
            </h2>

            <div className="row g-3">
              <div className="col-12 col-lg-4">
                <div className="card h-100 shadow-sm" style={{borderLeft: '4px solid #7c3aed'}}>
                  <div className="card-header text-white" style={{background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', fontWeight: 'bold'}}>
                    Volatility Regime Advisor
                  </div>
                  <div className="card-body" style={{backgroundColor: '#f5f3ff'}}>
                    <p className="mb-2"><strong>Regime:</strong> {phaseTwoAdvisors.regime?.regime ?? 'N/A'}</p>
                    <p className="mb-2"><strong>Confidence:</strong> {phaseTwoAdvisors.regime?.confidence ?? 'N/A'}</p>
                    <p className="mb-2"><strong>Trend Strength:</strong> {phaseTwoAdvisors.regime?.metrics?.trend_strength_pct ?? 'N/A'}%</p>
                    <p className="mb-2"><strong>Realized Vol (20d):</strong> {phaseTwoAdvisors.regime?.metrics?.realized_vol_20d ?? 'N/A'}%</p>
                    <p className="mb-2"><strong>VIX:</strong> {phaseTwoAdvisors.regime?.metrics?.vix ?? 'N/A'}</p>
                    <details open>
                      <summary style={{cursor: 'pointer', fontWeight: 600}}>Details</summary>
                      <ul className="mb-0 mt-2 ps-3" style={{fontSize: '0.85rem'}}>
                        {(phaseTwoAdvisors.regime?.playbook || []).map((item, idx) => (
                          <li key={`regime-playbook-${idx}`}>{item}</li>
                        ))}
                      </ul>
                    </details>
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-4">
                <div className="card h-100 shadow-sm" style={{borderLeft: '4px solid #0ea5e9'}}>
                  <div className="card-header text-white" style={{background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)', fontWeight: 'bold'}}>
                    Gap & Momentum Advisor
                  </div>
                  <div className="card-body" style={{backgroundColor: '#f0f9ff'}}>
                    <p className="mb-2"><strong>Setup:</strong> {phaseTwoAdvisors.gapMomentum?.classification ?? 'N/A'}</p>
                    <p className="mb-2"><strong>Continuation Probability:</strong> {phaseTwoAdvisors.gapMomentum?.continuation_probability ?? 'N/A'}%</p>
                    <p className="mb-2"><strong>Gap:</strong> {phaseTwoAdvisors.gapMomentum?.metrics?.gap_percent ?? 'N/A'}%</p>
                    <p className="mb-2"><strong>Volume Ratio:</strong> {phaseTwoAdvisors.gapMomentum?.metrics?.volume_ratio ?? 'N/A'}x</p>
                    <p className="mb-2"><strong>Momentum Bias:</strong> {phaseTwoAdvisors.gapMomentum?.momentum_bias ?? 'N/A'}</p>
                    <details open>
                      <summary style={{cursor: 'pointer', fontWeight: 600}}>Details</summary>
                      <div className="small mt-2">
                        <p className="mb-1"><strong>Continuation Trigger:</strong> ${phaseTwoAdvisors.gapMomentum?.trade_plan?.continuation_trigger ?? 'N/A'}</p>
                        <p className="mb-1"><strong>Reversal Trigger:</strong> ${phaseTwoAdvisors.gapMomentum?.trade_plan?.reversal_trigger ?? 'N/A'}</p>
                        <p className="mb-1"><strong>Invalidates Above:</strong> ${phaseTwoAdvisors.gapMomentum?.trade_plan?.invalidates_above ?? 'N/A'}</p>
                        <p className="mb-0"><strong>Invalidates Below:</strong> ${phaseTwoAdvisors.gapMomentum?.trade_plan?.invalidates_below ?? 'N/A'}</p>
                      </div>
                    </details>
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-4">
                <div className="card h-100 shadow-sm" style={{borderLeft: '4px solid #16a34a'}}>
                  <div className="card-header text-white" style={{background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', fontWeight: 'bold'}}>
                    Sector Rotation Advisor
                  </div>
                  <div className="card-body" style={{backgroundColor: '#f0fdf4'}}>
                    <p className="mb-2"><strong>Market Tone:</strong> {phaseTwoAdvisors.sectorRotation?.market_tone ?? 'N/A'}</p>
                    <p className="mb-2"><strong>Leading Sectors:</strong></p>
                    <div className="mb-2 d-flex flex-wrap gap-1">
                      {(phaseTwoAdvisors.sectorRotation?.leaders || []).slice(0, 3).map((sector) => (
                        <span key={sector.symbol} className="badge text-bg-success">{sector.name}</span>
                      ))}
                    </div>
                    <p className="mb-2"><strong>Lagging Sectors:</strong></p>
                    <div className="d-flex flex-wrap gap-1">
                      {(phaseTwoAdvisors.sectorRotation?.laggards || []).slice(0, 3).map((sector) => (
                        <span key={sector.symbol} className="badge text-bg-secondary">{sector.name}</span>
                      ))}
                    </div>
                    <details className="mt-2" open>
                      <summary style={{cursor: 'pointer', fontWeight: 600}}>Details</summary>
                      <div className="table-responsive mt-2">
                        <table className="table table-sm mb-0">
                          <thead>
                            <tr>
                              <th>Sector</th>
                              <th className="text-end">5d</th>
                              <th className="text-end">20d</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(phaseTwoAdvisors.sectorRotation?.sectors || []).slice(0, 5).map((sector) => (
                              <tr key={`sector-rank-${sector.symbol}`}>
                                <td>{sector.name}</td>
                                <td className="text-end">{sector.return_5d}%</td>
                                <td className="text-end">{sector.return_20d}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  </div>
                </div>
              </div>
            </div>

            <div className="alert alert-warning py-2 mt-3 mb-0" style={{ fontSize: '0.8rem' }}>
              Disclaimer: Regime and sector rotation insights are probabilistic and can change quickly with market conditions. Use position sizing and risk controls.
            </div>
          </div>
        </div>
      )}

      {stockData && !error && phaseThreeAdvisors && activeTab === 'phase3-advisors' && (
        <div className="mt-4" style={{width:'100%', maxWidth:'100%', overflowX:'hidden'}}>
          <div className="border rounded shadow-lg p-3 p-md-4 mx-2 mx-md-3" style={{overflowX:'hidden', background: '#fff', borderColor: '#fda4af'}}>
            <h2 className="h4 fw-bold mb-4 text-center p-3 rounded" style={{
              background: 'linear-gradient(135deg, #ffe4e6 0%, #fecdd3 100%)'
            }}>
              <span style={{fontSize: '2rem'}}>🧩</span>
              <span style={{
                background: 'linear-gradient(135deg, #be123c 0%, #9f1239 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginLeft: '0.5rem'
              }}>
                Risk Intelligence & Options Strategy
              </span>
            </h2>

            <div className="row g-3">
              <div className="col-12 col-lg-4">
                <div className="card h-100 shadow-sm" style={{borderLeft: '4px solid #f43f5e'}}>
                  <div className="card-header text-white" style={{background: 'linear-gradient(135deg, #f43f5e 0%, #be123c 100%)', fontWeight: 'bold'}}>
                    Earnings Event Advisor
                  </div>
                  <div className="card-body" style={{backgroundColor: '#fff1f2'}}>
                    <p className="mb-2"><strong>Event Risk:</strong> {phaseThreeAdvisors.earnings?.event_risk_score ?? 'N/A'}</p>
                    <p className="mb-2"><strong>Earnings Date:</strong> {phaseThreeAdvisors.earnings?.earnings_date ?? 'N/A'}</p>
                    <p className="mb-2"><strong>Implied Move:</strong> {phaseThreeAdvisors.earnings?.implied_move_percent ?? 'N/A'}%</p>
                    <p className="mb-2"><strong>Guidance:</strong> {phaseThreeAdvisors.earnings?.guidance ?? 'N/A'}</p>
                    <details open>
                      <summary style={{cursor: 'pointer', fontWeight: 600}}>Details</summary>
                      <div className="small mt-2">
                        <p className="mb-1"><strong>Risk Band:</strong> {(phaseThreeAdvisors.earnings?.risk_band || 'N/A').toUpperCase()}</p>
                        <p className="mb-1"><strong>Days To Earnings:</strong> {phaseThreeAdvisors.earnings?.days_to_earnings ?? 'N/A'}</p>
                        <p className="mb-1"><strong>Max Position %:</strong> {phaseThreeAdvisors.earnings?.pre_event_plan?.max_position_size_pct ?? 'N/A'}%</p>
                        <p className="mb-0"><strong>Overnight Policy:</strong> {phaseThreeAdvisors.earnings?.pre_event_plan?.overnight_policy ?? 'N/A'}</p>
                      </div>
                    </details>
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-4">
                <div className="card h-100 shadow-sm" style={{borderLeft: '4px solid #7c3aed'}}>
                  <div className="card-header text-white" style={{background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)', fontWeight: 'bold'}}>
                    AI Consensus Advisor
                  </div>
                  <div className="card-body" style={{backgroundColor: '#f5f3ff'}}>
                    <p className="mb-2"><strong>Verdict:</strong> {(phaseThreeAdvisors.consensus?.verdict || 'N/A').toUpperCase()}</p>
                    <p className="mb-2"><strong>Confidence:</strong> {phaseThreeAdvisors.consensus?.confidence ?? 'N/A'}</p>
                    <p className="mb-2"><strong>Score:</strong> {phaseThreeAdvisors.consensus?.score ?? 'N/A'}</p>
                    <p className="mb-2"><strong>Swing Vote:</strong> {phaseThreeAdvisors.consensus?.votes?.swing ?? 'N/A'}</p>
                    <details open>
                      <summary style={{cursor: 'pointer', fontWeight: 600}}>Details</summary>
                      <div className="small mt-2">
                        <p className="mb-1"><strong>Swing Label:</strong> {phaseThreeAdvisors.consensus?.vote_labels?.swing ?? 'N/A'}</p>
                        <p className="mb-1"><strong>Day Label:</strong> {phaseThreeAdvisors.consensus?.vote_labels?.day ?? 'N/A'}</p>
                        <p className="mb-1"><strong>Risk Label:</strong> {phaseThreeAdvisors.consensus?.vote_labels?.risk ?? 'N/A'}</p>
                        <p className="mb-1"><strong>2W Momentum:</strong> {phaseThreeAdvisors.consensus?.metrics?.momentum_pct_2w ?? 'N/A'}%</p>
                        <p className="mb-1"><strong>Day Move:</strong> {phaseThreeAdvisors.consensus?.metrics?.day_move_pct ?? 'N/A'}%</p>
                        <p className="mb-2"><strong>Action Plan:</strong></p>
                        <ul className="mb-0 ps-3">
                          {(phaseThreeAdvisors.consensus?.action_plan || []).map((step, idx) => (
                            <li key={`consensus-step-${idx}`}>{step}</li>
                          ))}
                        </ul>
                      </div>
                    </details>
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-4">
                <div className="card h-100 shadow-sm" style={{borderLeft: '4px solid #16a34a'}}>
                  <div className="card-header text-white" style={{background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', fontWeight: 'bold'}}>
                    Options Strategy Advisor
                  </div>
                  <div className="card-body" style={{backgroundColor: '#f0fdf4'}}>
                    <p className="mb-2"><strong>Outlook:</strong> {(phaseThreeAdvisors.options?.outlook || 'N/A').toUpperCase()}</p>
                    <p className="mb-2"><strong>Strategy:</strong> {phaseThreeAdvisors.options?.recommended_strategy ?? 'N/A'}</p>
                    <p className="mb-2"><strong>Strike Band:</strong> ${phaseThreeAdvisors.options?.suggested_strike_band?.low ?? 'N/A'} - ${phaseThreeAdvisors.options?.suggested_strike_band?.high ?? 'N/A'}</p>
                    <p className="mb-2"><strong>Expiry:</strong> {phaseThreeAdvisors.options?.suggested_expiry_window ?? 'N/A'}</p>
                    <details open>
                      <summary style={{cursor: 'pointer', fontWeight: 600}}>Details</summary>
                      <div className="small mt-2">
                        <p className="mb-1"><strong>Volatility % Price:</strong> {phaseThreeAdvisors.options?.volatility_percent_of_price ?? 'N/A'}%</p>
                        <p className="mb-1"><strong>Target Delta:</strong> {phaseThreeAdvisors.options?.positioning?.target_delta_range ?? 'N/A'}</p>
                        <p className="mb-1"><strong>Max Premium Budget:</strong> {phaseThreeAdvisors.options?.positioning?.max_premium_budget_pct ?? 'N/A'}%</p>
                        <p className="mb-2"><strong>Alternatives:</strong></p>
                        <ul className="mb-2 ps-3">
                          {(phaseThreeAdvisors.options?.alternative_strategies || []).map((alt, idx) => (
                            <li key={`option-alt-${idx}`}>{alt}</li>
                          ))}
                        </ul>
                        <p className="mb-0"><strong>Risk Note:</strong> {phaseThreeAdvisors.options?.risk_note ?? 'N/A'}</p>
                      </div>
                    </details>
                  </div>
                </div>
              </div>
            </div>

            <div className="alert alert-warning py-2 mt-3 mb-0" style={{ fontSize: '0.8rem' }}>
              Disclaimer: Risk Intelligence and Options Strategy content is informational only and does not guarantee outcomes. Options carry significant risk.
            </div>
          </div>
        </div>
      )}

      {/* Company News Tab */}
      {stockData && !error && activeTab === 'company-news' && (
        <div className="mt-4" style={{width:'100%', maxWidth:'100%', overflowX:'hidden'}}>
          <div className="border border-info-subtle rounded shadow-lg bg-white p-3 p-md-4 mx-2 mx-md-3">
            <h2 className="h4 fw-bold mb-4 text-center d-flex align-items-center justify-content-center p-3 rounded" style={{background: 'linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)'}}>
              <span className="me-2" style={{fontSize: '2rem'}}>📰</span>
              <span style={{color: '#fff', textShadow: '2px 2px 4px rgba(0,0,0,0.3)'}}>
                Recent Company News - {selectedStock}
              </span>
              {companyNews && companyNews.company_name && (
                <span className="ms-2 text-white" style={{fontSize: '1rem', opacity: 0.9}}>
                  ({companyNews.company_name})
                </span>
              )}
            </h2>
            
            {newsLoading && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status" style={{width: '3rem', height: '3rem'}}>
                  <span className="visually-hidden">Loading news...</span>
                </div>
                <p className="mt-3 text-muted fw-semibold">Fetching recent company news from Yahoo Finance and Google News...</p>
                <p className="text-muted small">Searching for earnings, announcements, partnerships, and market updates</p>
              </div>
            )}

            {!newsLoading && companyNews && companyNews.error && (
              <div className="alert alert-warning" role="alert">
                <strong>⚠️ Error:</strong> {companyNews.error}
              </div>
            )}

            {!newsLoading && companyNews && !companyNews.error && (
              <>
                {companyNews.last_updated && (
                  <div className="text-center mb-3">
                    <span className="badge bg-secondary text-white px-2 py-1 me-1" style={{fontSize: '0.75rem'}}>
                      📅 {new Date(companyNews.last_updated).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <span className="badge bg-primary text-white px-2 py-1 me-1" style={{fontSize: '0.75rem'}}>
                      📊 {companyNews.news_count} articles
                    </span>
                    {/* News Source Statistics - Inline */}
                    {companyNews.news && companyNews.news.length > 0 && (() => {
                      const yahooCount = companyNews.news.filter(n => n.source === 'Yahoo Finance').length;
                      const googleCount = companyNews.news.filter(n => n.source === 'Internet Search').length;
                      
                      return (
                        <>
                          {yahooCount > 0 && (
                            <span className="badge bg-info text-white px-2 py-1 me-1" style={{fontSize: '0.75rem'}}>
                              📈 {yahooCount}
                            </span>
                          )}
                          {googleCount > 0 && (
                            <span className="badge bg-warning text-dark px-2 py-1" style={{fontSize: '0.75rem'}}>
                              🌐 {googleCount}
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Remove the old News Source Statistics section */}

                {companyNews.news && companyNews.news.length === 0 && (
                  <div className="alert alert-info text-center" role="alert">
                    <h5>ℹ️ No News Available</h5>
                    <p className="mb-0">There are currently no news articles available for {selectedStock}.</p>
                  </div>
                )}

                {companyNews.news && companyNews.news.length > 0 && (
                  <div className="row g-4">
                    {companyNews.news.map((article, index) => (
                      <div key={index} className="col-12 col-md-6 col-lg-4">
                        <div 
                          className="card h-100 shadow-sm hover-shadow" 
                          style={{
                            transition: 'all 0.3s ease',
                            borderTop: '2px solid #0d6efd',
                            borderRight: '2px solid #0d6efd',
                            borderBottom: '2px solid #0d6efd',
                            borderLeft: '2px solid #0d6efd'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-5px)';
                            e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '';
                          }}
                        >
                          {article.thumbnail && (
                            <img 
                              src={article.thumbnail} 
                              className="card-img-top" 
                              alt={article.title}
                              style={{height: '180px', objectFit: 'cover'}}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                          <div className="card-body d-flex flex-column">
                            <div className="mb-2 d-flex flex-wrap gap-2 align-items-center">
                              <span className={`badge ${article.source === 'Yahoo Finance' ? 'bg-primary' : 'bg-success'}`}>
                                {article.source === 'Yahoo Finance' ? '📈 Yahoo' : '🌐 Google News'}
                              </span>
                              <small className="text-muted" style={{fontSize: '0.75rem'}}>
                                📅 {article.published_date}
                              </small>
                            </div>
                            <h5 className="card-title" style={{
                              fontSize: '0.95rem',
                              lineHeight: '1.4',
                              minHeight: '2.8rem',
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              fontWeight: '600'
                            }}>
                              {article.title}
                            </h5>
                            
                            {/* Detailed content/description */}
                            {(article.description || article.summary) && (
                              <p 
                                className="card-text text-muted small mb-2" 
                                style={{
                                  fontSize: '0.85rem',
                                  lineHeight: '1.5',
                                  overflow: 'hidden',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 4,
                                  WebkitBoxOrient: 'vertical'
                                }}
                              >
                                {article.description || article.summary}
                              </p>
                            )}
                            
                            <div className="mt-auto">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <small className="text-muted">
                                  <strong>📰 {article.publisher}</strong>
                                </small>
                              </div>
                              {article.related_tickers && article.related_tickers.length > 0 && (
                                <div className="mb-2">
                                  <small className="text-muted d-block mb-1">Related:</small>
                                  <div className="d-flex flex-wrap gap-1">
                                    {article.related_tickers.slice(0, 3).map((ticker, idx) => (
                                      <span key={idx} className="badge bg-secondary" style={{fontSize: '0.7rem'}}>
                                        {ticker}
                                      </span>
                                    ))}
                                    {article.related_tickers.length > 3 && (
                                      <span className="badge bg-secondary" style={{fontSize: '0.7rem'}}>
                                        +{article.related_tickers.length - 3} more
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                              {article.link && article.link !== '#' && (
                                <div className="d-flex gap-1 mt-2 justify-content-center">
                                  <a 
                                    href={article.link} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className={`btn btn-sm ${article.source === 'Yahoo Finance' ? 'btn-primary' : 'btn-success'}`}
                                    style={{
                                      background: article.source === 'Yahoo Finance' 
                                        ? 'linear-gradient(90deg, #0d6efd, #0a58ca)' 
                                        : 'linear-gradient(90deg, #198754, #157347)',
                                      border: 'none',
                                      fontSize: '0.7rem',
                                      padding: '0.25rem 0.4rem',
                                      flex: '0 0 auto'
                                    }}
                                  >
                                    <span style={{fontSize: '0.75rem'}}>🗗</span> Open in New Tab
                                  </a>
                                  <button
                                    onClick={() => {
                                      window.open(article.link, 'articleWindow', 'width=1000,height=800,scrollbars=yes,resizable=yes,toolbar=no,location=yes,menubar=no');
                                    }}
                                    className="btn btn-sm"
                                    style={{
                                      fontSize: '0.7rem',
                                      padding: '0.25rem 0.4rem',
                                      background: 'linear-gradient(90deg, #6c757d, #5a6268)',
                                      color: '#fff',
                                      border: 'none',
                                      flex: '0 0 auto'
                                    }}
                                  >
                                    <span style={{fontSize: '0.75rem'}}>🗔</span> Open in Popup
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Printable View Tab */}
      {stockData && !error && activeTab === 'printable' && (
        <div className="mt-4" style={{width:'100%', maxWidth:'100%', overflowX:'hidden'}}>
          <div className="mx-2 mx-md-auto" style={{maxWidth: '95%'}}>
            <div className="d-print-none mb-3 d-flex justify-content-end gap-2">
              <button 
                className="btn btn-lg shadow-lg"
                onClick={handlePrint}
                title="Open browser print dialog to print or save as PDF"
                style={{
                  background: 'linear-gradient(135deg,#667eea 0%,#764ba2 100%)',
                  border: 'none',
                  color: '#fff',
                  fontWeight: '600',
                  padding: '0.75rem 2rem',
                  borderRadius: '8px',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                Print / Save as PDF
              </button>
            </div>
          
            <div className="bg-white shadow-lg" style={{pageBreakAfter: 'auto', border: '1px solid #e5e7eb'}}>
            {/* Professional Header */}
            <div className="text-center mb-0 pb-4" style={{
              background: 'linear-gradient(135deg,#1e3a8a 0%,#3b82f6 50%,#1e40af 100%)',
              padding: '3rem 2rem',
              borderBottom: '4px solid #1e40af'
            }}>
              <div style={{backgroundColor: 'rgba(255,255,255,0.15)', display: 'inline-block', padding: '0.5rem 1.5rem', borderRadius: '30px', marginBottom: '1rem'}}>
                <p className="mb-0 text-white fw-bold" style={{fontSize: '0.85rem', letterSpacing: '2px'}}>COMPREHENSIVE STOCK ANALYSIS</p>
              </div>
              <h1 className="h1 mb-2 fw-bold text-white" style={{fontSize: '2.5rem', textShadow: '2px 2px 8px rgba(0,0,0,0.3)', letterSpacing: '1px'}}>
                {stockData.name}
              </h1>
              <h2 className="h3 mb-3 text-white" style={{fontWeight: '500', opacity: '0.95'}}>
                Symbol: {stockData.symbol}
              </h2>
              {stockData.last_updated && (
                <div style={{backgroundColor: 'rgba(255,255,255,0.25)', display: 'inline-block', padding: '0.6rem 1.5rem', borderRadius: '25px', marginTop: '1rem'}}>
                  <p className="mb-0 text-white fw-semibold" style={{fontSize: '0.9rem'}}>
                    Report Generated: {stockData.last_updated}
                  </p>
                </div>
              )}
            </div>

            {/* Executive Summary Box */}
            <div className="px-4 px-md-5 pt-4">
              <div className="mb-4 p-4 rounded shadow-sm" style={{
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                border: '2px solid #0284c7',
                borderLeft: '6px solid #0284c7'
              }}>
                <h3 className="h5 mb-3 fw-bold d-flex align-items-center" style={{color: '#0c4a6e'}}>
                  Executive Summary
                </h3>
                <div className="row g-3">
                  <div className="col-6 col-md-3">
                    <div className="text-center p-3 rounded" style={{backgroundColor: 'rgba(255,255,255,0.8)'}}>
                      <small className="d-block text-muted fw-semibold mb-1" style={{fontSize: '0.75rem'}}>CURRENT PRICE</small>
                      <div className="fw-bold" style={{fontSize: '1.5rem', color: '#0284c7'}}>${stockData.price}</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-center p-3 rounded" style={{backgroundColor: 'rgba(255,255,255,0.8)'}}>
                      <small className="d-block text-muted fw-semibold mb-1" style={{fontSize: '0.75rem'}}>DAILY CHANGE</small>
                      <div className={`fw-bold ${stockData.change >= 0 ? 'text-success' : 'text-danger'}`} style={{fontSize: '1.2rem'}}>
                        {stockData.change > 0 ? '▲' : '▼'} {parseFloat(stockData.percent_change).toFixed(2)}%
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-center p-3 rounded" style={{backgroundColor: 'rgba(255,255,255,0.8)'}}>
                      <small className="d-block text-muted fw-semibold mb-1" style={{fontSize: '0.75rem'}}>MARKET CAP</small>
                      <div className="fw-bold" style={{fontSize: '1.1rem', color: '#0c4a6e'}}>{formatLargeNumber(stockData.market_cap)}</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-center p-3 rounded" style={{backgroundColor: 'rgba(255,255,255,0.8)'}}>
                      <small className="d-block text-muted fw-semibold mb-1" style={{fontSize: '0.75rem'}}>VOLUME</small>
                      <div className="fw-bold" style={{fontSize: '1.1rem', color: '#0c4a6e'}}>{formatLargeNumber(stockData.volume)}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detailed Stock Metrics */}
              <div className="mb-4">
                <h3 className="h5 mb-3 fw-bold d-flex align-items-center pb-2" style={{
                  color: '#1f2937',
                  borderBottom: '3px solid #3b82f6'
                }}>
                  Key Financial Metrics
                </h3>
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <div className="p-3 rounded shadow-sm h-100" style={{backgroundColor: '#f9fafb', border: '1px solid #e5e7eb'}}>
                      <div className="d-flex justify-content-between align-items-center mb-2 pb-2" style={{borderBottom: '2px solid #e5e7eb'}}>
                        <span className="fw-semibold" style={{color: '#6b7280'}}>Company Name</span>
                        <span className="fw-bold" style={{color: '#1f2937'}}>({stockData.symbol}) {stockData.name}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2 pb-2" style={{borderBottom: '2px solid #e5e7eb'}}>
                        <span className="fw-semibold" style={{color: '#6b7280'}}>Current Price</span>
                        <span className="fw-bold text-primary" style={{fontSize: '1.1rem'}}>${stockData.price}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-semibold" style={{color: '#6b7280'}}>Price Change</span>
                        <span className={`fw-bold ${stockData.change >= 0 ? 'text-success' : 'text-danger'}`}>
                          {stockData.change > 0 ? '▲' : '▼'} ${Math.abs(stockData.change).toFixed(2)} ({parseFloat(stockData.percent_change).toFixed(2)}%)
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="p-3 rounded shadow-sm h-100" style={{backgroundColor: '#f9fafb', border: '1px solid #e5e7eb'}}>
                      <div className="d-flex justify-content-between align-items-center mb-2 pb-2" style={{borderBottom: '2px solid #e5e7eb'}}>
                        <span className="fw-semibold" style={{color: '#6b7280'}}>Day's Range</span>
                        <span className="fw-bold" style={{color: '#1f2937'}}>${parseFloat(stockData.day_low).toFixed(2)} - ${parseFloat(stockData.day_high).toFixed(2)}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2 pb-2" style={{borderBottom: '2px solid #e5e7eb'}}>
                        <span className="fw-semibold" style={{color: '#6b7280'}}>Market Cap</span>
                        <span className="fw-bold" style={{color: '#1f2937'}}>{formatLargeNumber(stockData.market_cap)}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-semibold" style={{color: '#6b7280'}}>Trading Volume</span>
                        <span className="fw-bold" style={{color: '#1f2937'}}>{formatLargeNumber(stockData.volume)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Historical Performance */}
            {historicalData && (
              <div className="px-4 px-md-5 mb-4" style={{pageBreakInside: 'avoid'}}>
                <h3 className="h5 mb-3 fw-bold d-flex align-items-center pb-2" style={{
                  color: '#1f2937',
                  borderBottom: '3px solid #10b981'
                }}>
                  Historical Performance Analysis
                </h3>
                <div className="row g-3">
                  {/* 1 Week */}
                  {historicalData.week && (
                    <div className="col-12 col-md-4">
                      <div className="p-3 rounded shadow-sm" style={{backgroundColor: '#e0f2fe', border: '2px solid #bae6fd'}}>
                        <h4 className="h6 fw-bold mb-2 d-flex align-items-center" style={{color: '#0369a1', backgroundColor: '#f0f9ff', padding: '0.5rem', borderRadius: '5px'}}>
                          1 WEEK PERFORMANCE
                        </h4>
                        {historicalData.week.start_date && (
                          <p className="small mb-2 fst-italic" style={{backgroundColor: 'rgba(255,255,255,0.8)', padding: '0.3rem 0.5rem', borderRadius: '3px', color: '#475569'}}>
                            {historicalData.week.start_date} to {historicalData.week.end_date}
                          </p>
                        )}
                        <table className="table table-sm mb-0 bg-white rounded shadow-sm">
                          <tbody>
                            <tr style={{backgroundColor: '#f8fafc'}}><td className="fw-semibold" style={{color: '#475569'}}>High:</td><td className="text-end text-success fw-bold">${historicalData.week.high}</td></tr>
                            <tr><td className="fw-semibold" style={{color: '#475569'}}>Low:</td><td className="text-end text-danger fw-bold">${historicalData.week.low}</td></tr>
                            <tr style={{backgroundColor: '#f8fafc'}}><td className="fw-semibold" style={{color: '#475569'}}>Avg Price:</td><td className="text-end fw-semibold">${historicalData.week.avg_price}</td></tr>
                            <tr><td className="fw-semibold" style={{color: '#475569'}}>Change:</td><td className={`text-end fw-bold ${historicalData.week.price_change >= 0 ? 'text-success' : 'text-danger'}`}>
                              {historicalData.week.price_change >= 0 ? '▲' : '▼'} ${Math.abs(historicalData.week.price_change)} ({historicalData.week.price_change_percent}%)
                            </td></tr>
                            <tr style={{backgroundColor: '#f8fafc'}}><td className="fw-semibold" style={{color: '#475569'}}>Volatility:</td><td className="text-end fw-semibold">${historicalData.week.volatility}</td></tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* 2 Weeks */}
                  {historicalData.two_weeks && (
                    <div className="col-12 col-md-4">
                      <div className="p-3 rounded shadow-sm" style={{backgroundColor: '#fef3c7', border: '2px solid #fde68a'}}>
                        <h4 className="h6 fw-bold mb-2 d-flex align-items-center" style={{color: '#92400e', backgroundColor: '#fffbeb', padding: '0.5rem', borderRadius: '5px'}}>
                          2 WEEKS PERFORMANCE
                        </h4>
                        {historicalData.two_weeks.start_date && (
                          <p className="small mb-2 fst-italic" style={{backgroundColor: 'rgba(255,255,255,0.8)', padding: '0.3rem 0.5rem', borderRadius: '3px', color: '#475569'}}>
                            {historicalData.two_weeks.start_date} to {historicalData.two_weeks.end_date}
                          </p>
                        )}
                        <table className="table table-sm mb-0 bg-white rounded shadow-sm">
                          <tbody>
                            <tr style={{backgroundColor: '#f8fafc'}}><td className="fw-semibold" style={{color: '#475569'}}>High:</td><td className="text-end text-success fw-bold">${historicalData.two_weeks.high}</td></tr>
                            <tr><td className="fw-semibold" style={{color: '#475569'}}>Low:</td><td className="text-end text-danger fw-bold">${historicalData.two_weeks.low}</td></tr>
                            <tr style={{backgroundColor: '#f8fafc'}}><td className="fw-semibold" style={{color: '#475569'}}>Avg Price:</td><td className="text-end fw-semibold">${historicalData.two_weeks.avg_price}</td></tr>
                            <tr><td className="fw-semibold" style={{color: '#475569'}}>Change:</td><td className={`text-end fw-bold ${historicalData.two_weeks.price_change >= 0 ? 'text-success' : 'text-danger'}`}>
                              {historicalData.two_weeks.price_change >= 0 ? '▲' : '▼'} ${Math.abs(historicalData.two_weeks.price_change)} ({historicalData.two_weeks.price_change_percent}%)
                            </td></tr>
                            <tr style={{backgroundColor: '#f8fafc'}}><td className="fw-semibold" style={{color: '#475569'}}>Volatility:</td><td className="text-end fw-semibold">${historicalData.two_weeks.volatility}</td></tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* 1 Month */}
                  {historicalData.month && (
                    <div className="col-12 col-md-4">
                      <div className="p-3 rounded shadow-sm" style={{backgroundColor: '#d1fae5', border: '2px solid #a7f3d0'}}>
                        <h4 className="h6 fw-bold mb-2 d-flex align-items-center" style={{color: '#065f46', backgroundColor: '#ecfdf5', padding: '0.5rem', borderRadius: '5px'}}>
                          1 MONTH PERFORMANCE
                        </h4>
                        {historicalData.month.start_date && (
                          <p className="small mb-2 fst-italic" style={{backgroundColor: 'rgba(255,255,255,0.8)', padding: '0.3rem 0.5rem', borderRadius: '3px', color: '#475569'}}>
                            {historicalData.month.start_date} to {historicalData.month.end_date}
                          </p>
                        )}
                        <table className="table table-sm mb-0 bg-white rounded shadow-sm">
                          <tbody>
                            <tr style={{backgroundColor: '#f8fafc'}}><td className="fw-semibold" style={{color: '#475569'}}>High:</td><td className="text-end text-success fw-bold">${historicalData.month.high}</td></tr>
                            <tr><td className="fw-semibold" style={{color: '#475569'}}>Low:</td><td className="text-end text-danger fw-bold">${historicalData.month.low}</td></tr>
                            <tr style={{backgroundColor: '#f8fafc'}}><td className="fw-semibold" style={{color: '#475569'}}>Avg Price:</td><td className="text-end fw-semibold">${historicalData.month.avg_price}</td></tr>
                            <tr><td className="fw-semibold" style={{color: '#475569'}}>Change:</td><td className={`text-end fw-bold ${historicalData.month.price_change >= 0 ? 'text-success' : 'text-danger'}`}>
                              {historicalData.month.price_change >= 0 ? '▲' : '▼'} ${Math.abs(historicalData.month.price_change)} ({historicalData.month.price_change_percent}%)
                            </td></tr>
                            <tr style={{backgroundColor: '#f8fafc'}}><td className="fw-semibold" style={{color: '#475569'}}>Volatility:</td><td className="text-end fw-semibold">${historicalData.month.volatility}</td></tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 30-Day Chart */}
            {historicalData && historicalData.chart_data && historicalData.chart_data.length > 0 && (
              <div className="px-4 px-md-5 mb-4">
                <div className="p-3 rounded shadow-sm" style={{pageBreakInside: 'avoid', backgroundColor: '#f0f9ff', border: '2px solid #bae6fd'}}>
                <h3 className="h5 mb-3 fw-bold d-flex align-items-center" style={{color: '#0369a1'}}>
                  {selectedStock} Price Chart (Last 30 Days)
                </h3>
                <div className="bg-white p-2 rounded shadow-sm" style={{overflow: 'hidden'}}>
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={historicalData.chart_data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12, fill: '#666' }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        stroke="#666"
                      />
                      <YAxis 
                        domain={['auto', 'auto']}
                        tick={{ fontSize: 12, fill: '#666' }} 
                        stroke="#666" 
                      />
                      <Tooltip 
                        formatter={(value) => `$${value}`}
                        contentStyle={{ backgroundColor: '#f5f5f5', border: '2px solid #2196f3', borderRadius: '8px', fontWeight: 'bold' }}
                      />
                      <Legend wrapperStyle={{ paddingTop: '10px', fontWeight: 'bold' }} />
                      <Line type="monotone" dataKey="close" stroke="#0d6efd" strokeWidth={2} name="Close Price" dot={false} />
                      <Line type="monotone" dataKey="high" stroke="#198754" strokeWidth={1} name="High" dot={false} strokeDasharray="5 5" />
                      <Line type="monotone" dataKey="low" stroke="#dc3545" strokeWidth={1} name="Low" dot={false} strokeDasharray="5 5" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              </div>
            )}

            {/* 1-Year Chart */}
            {historicalData && historicalData.chart_data_year && historicalData.chart_data_year.length > 0 && (
              <div className="px-4 px-md-5 mb-4">
                <div className="p-3 rounded shadow-sm" style={{pageBreakBefore: 'always', pageBreakInside: 'avoid', backgroundColor: '#ecfdf5', border: '2px solid #a7f3d0'}}>
                <h3 className="h5 mb-3 fw-bold d-flex align-items-center" style={{color: '#065f46'}}>
                  {selectedStock} Price Chart (Last 1 Year)
                </h3>
                <div className="bg-white p-2 rounded shadow-sm" style={{overflow: 'hidden'}}>
                  <ResponsiveContainer width="100%" height={450}>
                    <LineChart data={historicalData.chart_data_year} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 11, fill: '#666' }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        interval="preserveStartEnd"
                        stroke="#666"
                      />
                      <YAxis 
                        domain={['auto', 'auto']}
                        tick={{ fontSize: 12, fill: '#666' }} 
                        stroke="#666" 
                      />
                      <Tooltip 
                        formatter={(value) => `$${value}`}
                        contentStyle={{ backgroundColor: '#f5f5f5', border: '2px solid #4caf50', borderRadius: '8px', fontWeight: 'bold' }}
                      />
                      <Legend wrapperStyle={{ paddingTop: '10px', fontWeight: 'bold' }} />
                      <Line type="monotone" dataKey="close" stroke="#198754" strokeWidth={2} name="Close Price" dot={false} />
                      <Line type="monotone" dataKey="high" stroke="#0d6efd" strokeWidth={1} name="High" dot={false} strokeDasharray="5 5" />
                      <Line type="monotone" dataKey="low" stroke="#dc3545" strokeWidth={1} name="Low" dot={false} strokeDasharray="5 5" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              </div>
            )}

            {/* AI Analysis */}
            {aiAnalysis && (
              <div className="px-4 px-md-5 mb-4" style={{pageBreakBefore: 'always'}}>
                <div>
                <h3 className="h5 mb-3 fw-bold d-flex align-items-center" style={{
                  color: '#764ba2',
                  borderBottom: '3px solid #764ba2',
                  paddingBottom: '0.5rem'
                }}>
                  Swing Trade Advisor
                </h3>
                
                {/* Section View Data */}
                {aiAnalysisStructured && (
                  <div className="mb-4">
                    {[
                      {
                        title: 'Brief Analysis of the Stock\'s Current Trend',
                        key: 'current_trend_analysis',
                        color: '#3b82f6'
                      },
                      {
                        title: 'Key Support and Resistance Levels',
                        key: 'key_support_resistance_levels',
                        color: '#4ade80'
                      },
                      {
                        title: 'Potential Swing Trading Opportunities',
                        key: 'potential_swing_trading_opportunities',
                        color: '#10b981'
                      },
                      {
                        title: 'Risk Assessment for Swing Trading',
                        key: 'risk_assessment',
                        color: '#f59e0b'
                      },
                      {
                        title: 'Recommended Timeframe for Swing Trades',
                        key: 'recommended_timeframe',
                        color: '#ec4899'
                      },
                      {
                        title: 'Prediction for the Stock in Next Two Weeks',
                        key: 'two_week_prediction',
                        color: '#14b8a6'
                      }
                    ].map((section, index) => {
                      const content = aiAnalysisStructured[section.key];
                      if (!content) return null;
                      
                      return (
                        <div key={section.key} className="mb-3 p-3 rounded shadow-sm" style={{
                          pageBreakInside: 'avoid',
                          border: `2px solid ${section.color}`,
                          backgroundColor: '#f8f9fa'
                        }}>
                          <h4 className="h6 fw-bold mb-2" style={{color: section.color}}>
                            {section.title}
                          </h4>
                          <div style={{fontSize: '0.85rem', color: '#374151'}}>
                            {content}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Plain Analysis Text */}
                <div className="mt-4">
                  <h4 className="h6 fw-bold mb-3" style={{color: '#6b7280'}}>Complete Analysis</h4>
                  <div style={{fontSize: '0.9rem'}}>
                    {parseAiAnalysis(aiAnalysis) || (
                      <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.85rem' }}>
                        {aiAnalysis.replace(/\*\*\*/g, '').replace(/\*\*/g, '').replace(/\*/g, '')}
                      </pre>
                    )}
                  </div>
                </div>
              </div>
              </div>
            )}

            {/* Day Trade Advisor */}
            {dayAiAnalysis && (
              <div className="px-4 px-md-5 mb-4" style={{pageBreakBefore: 'always'}}>
                <div>
                <h3 className="h5 mb-3 fw-bold d-flex align-items-center" style={{
                  color: '#c2410c',
                  borderBottom: '3px solid #c2410c',
                  paddingBottom: '0.5rem'
                }}>
                  Day Trade Advisor
                </h3>

                {dayAiAnalysisStructured && (
                  <div className="mb-4">
                    {[
                      { title: 'Current Trend Analysis', key: 'current_trend_analysis', color: '#ea580c' },
                      { title: 'Key Support and Resistance Levels', key: 'key_support_resistance_levels', color: '#f97316' },
                      { title: 'Potential Day Trading Opportunities', key: 'potential_swing_trading_opportunities', color: '#fb923c' },
                      { title: 'Risk Assessment for Day Trading', key: 'risk_assessment', color: '#ef4444' },
                      { title: 'Recommended Timeframe for Day Trades', key: 'recommended_timeframe', color: '#d97706' },
                      { title: 'Prediction for the Stock by Market Close', key: 'two_week_prediction', color: '#c2410c' }
                    ].map((section, index) => {
                      const content = dayAiAnalysisStructured[section.key];
                      if (!content) return null;

                      return (
                        <div key={section.key} className="mb-3 p-3 rounded shadow-sm" style={{
                          pageBreakInside: 'avoid',
                          border: `2px solid ${section.color}`,
                          backgroundColor: '#fff7ed'
                        }}>
                          <h4 className="h6 fw-bold mb-2" style={{color: section.color}}>
                            {section.title}
                          </h4>
                          <div style={{fontSize: '0.85rem', color: '#374151'}}>
                            {content}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="mt-4">
                  <h4 className="h6 fw-bold mb-3" style={{color: '#6b7280'}}>Complete Analysis</h4>
                  <div style={{fontSize: '0.9rem'}}>
                    {parseAiAnalysis(dayAiAnalysis) || (
                      <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.85rem' }}>
                        {dayAiAnalysis.replace(/\*\*\*/g, '').replace(/\*\*/g, '').replace(/\*/g, '')}
                      </pre>
                    )}
                  </div>
                </div>
              </div>
              </div>
            )}

            {/* Centiment Advisor */}
            {centimentAnalysis && (
              <div className="px-4 px-md-5 mb-4" style={{pageBreakBefore: 'always'}}>
                <div>
                <h3 className="h5 mb-3 fw-bold d-flex align-items-center" style={{
                  color: '#115e59',
                  borderBottom: '3px solid #115e59',
                  paddingBottom: '0.5rem'
                }}>
                  Centiment Advisor
                </h3>

                {centimentAnalysisStructured && (
                  <div className="mb-4">
                    {[
                      { title: 'Overall Market Sentiment', key: 'overall_market_sentiment', color: '#0f766e' },
                      { title: 'News and Narrative Drivers', key: 'news_and_narrative_drivers', color: '#14b8a6' },
                      { title: 'Investor Emotion Signals', key: 'investor_emotion_signals', color: '#0d9488' },
                      { title: 'Potential Price Reaction (Near Term)', key: 'potential_price_reaction', color: '#0284c7' },
                      { title: 'Sentiment Risk Factors', key: 'sentiment_risk_factors', color: '#dc2626' },
                      { title: 'Centiment Advisor Outlook', key: 'centiment_advisor_outlook', color: '#115e59' }
                    ].map((section, index) => {
                      const content = centimentAnalysisStructured[section.key];
                      if (!content) return null;

                      return (
                        <div key={section.key} className="mb-3 p-3 rounded shadow-sm" style={{
                          pageBreakInside: 'avoid',
                          border: `2px solid ${section.color}`,
                          backgroundColor: '#f0fdfa'
                        }}>
                          <h4 className="h6 fw-bold mb-2" style={{color: section.color}}>
                            {section.title}
                          </h4>
                          <div style={{fontSize: '0.85rem', color: '#374151'}}>
                            {content}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="mt-4">
                  <h4 className="h6 fw-bold mb-3" style={{color: '#6b7280'}}>Complete Analysis</h4>
                  <div style={{fontSize: '0.9rem'}}>
                    {parseAiAnalysis(centimentAnalysis) || (
                      <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.85rem' }}>
                        {centimentAnalysis.replace(/\*\*\*/g, '').replace(/\*\*/g, '').replace(/\*/g, '')}
                      </pre>
                    )}
                  </div>
                </div>
              </div>
              </div>
            )}

            {/* Professional Footer */}
            <div className="px-4 px-md-5 pb-4">
              <div className="text-center pt-4 mt-4" style={{
                borderTop: '3px solid #3b82f6',
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                padding: '2rem',
                borderRadius: '8px'
              }}>
                <div className="mb-3">
                  <h5 className="fw-bold mb-2" style={{color: '#1e40af'}}>AllTechSoft TradeAvisor AI</h5>
                  <p className="mb-1 text-muted small">Professional Stock Analysis & Trading Insights</p>
                </div>
                <div className="small text-muted" style={{fontSize: '0.8rem'}}>
                  <p className="mb-1">
                    <strong>Disclaimer:</strong> This report is generated for informational purposes only.
                  </p>
                  <p className="mb-0">
                    Data and analysis provided as-is. Please consult with a qualified financial advisor before making investment decisions.
                  </p>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;